import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tooltip,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  HistoryOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CostSummary from '../components/CostSummary';
import LoadingCard from '../components/LoadingCard';
import MarketingVideoHistoryScreen from '../MarketingVideoHistoryScreen';
import type { CreditBalance } from '../types';
import type {
  MarketingActionSignal,
  MarketingCharacterAsset,
  MarketingInspirationItem,
  MarketingVideoRecord,
  MarketingVideoPresetFamily,
  MarketingVideoPromptTemplates,
  MarketingVideoSimpleConfig,
  MarketingVideoSimpleGoal,
  MarketingVideoSimpleGoalConfig,
  MarketingVideoStyleId,
  MarketingVideoUiMode,
} from './types';
import { buildMarketingInspirationContext, sortMarketingInspirationItems } from './inspiration';
import MarketingInspirationPicker from './MarketingInspirationPicker';

const { Text } = Typography;
const { TextArea } = Input;

interface MarketingVideoModelCatalogItem {
  id: string;
  displayName: string;
  catalogStatus: string;
  uiVisible: boolean;
  replacementModel?: string | null;
  defaultResolution: string;
  defaultDurationSeconds: number;
  supportedDurations: number[];
  supportedResolutions: string[];
  supportedSizes?: string[];
  supportedAspectRatios: string[];
  supportsFrameImages: boolean;
  supportsReferenceImages: boolean;
  supportsAudio: boolean;
  audioOptional?: boolean;
  supportsFirstFrameImage?: boolean;
  supportsLastFrameImage?: boolean;
  recommendedForSimple?: number | null;
  pricingKind: 'per_second' | 'token_formula';
  pricingConfig?: {
    ratesByOutput?: Record<string, { audioOn?: number; audioOff?: number }>;
    defaultAudioOn?: number | null;
    defaultAudioOff?: number | null;
    usdPerMillionTokens?: number | null;
    fps?: number | null;
    divisor?: number | null;
    bufferMultiplier?: number | null;
  };
}

interface MarketingVideoModuleProps {
  apiBase: string;
  bookId: string;
  userId: string;
  bookTitle?: string;
  sourceLanguage?: string;
  selectedLanguage?: string;
  actionSignal?: MarketingActionSignal | null;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance?: (userId: string) => Promise<void> | void;
  initialStudioView?: 'create' | 'library';
  allowedStudioViews?: Array<'create' | 'library'>;
  libraryTitle?: string;
  initialSimpleGoalId?: MarketingVideoSimpleGoal;
  initialGoalRequestId?: number;
  inspirationRefreshSignal?: number;
  onOpenSourceKit?: () => void;
}

type MarketingStudioView = 'create' | 'library';

const VIDEO_STUDIO_UI_MODE_STORAGE_KEY = 'marketing_video_ui_mode';
const USD_PER_CREDIT_FALLBACK = 0.00004;
const COVER_ANIMATE_PRIMER_SECONDS = 4;
const COVER_ANIMATE_MULTI_PASS_ENABLED = false;
const MAX_CHARACTER_REFERENCE_ASSETS = 4;
const DEFAULT_VIDEO_STYLE: MarketingVideoStyleId = 'realistic';
const CHARACTER_DESCRIPTION_FALLBACK_HEADING =
  'Character description fallback: the selected portraits cannot be sent to this model, so use these written character cues instead.';
type PromptConditioningMode =
  | 'textOnly'
  | 'referenceImage'
  | 'characterReference'
  | 'coverAndCharacterReference'
  | 'firstFrame'
  | 'lastFrame'
  | 'firstAndLastFrame';
type PresetCollectionFilter = 'featured' | 'all' | MarketingVideoPresetFamily;

const getErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return String(data?.detail || data?.message || fallback);
  } catch {
    return fallback;
  }
};

const VIDEO_STYLE_OPTIONS: Array<{
  value: MarketingVideoStyleId;
  label: string;
  promptInstruction: string;
}> = [
  {
    value: 'realistic',
    label: 'Realistic',
    promptInstruction:
      'Style mandate: photorealistic cinematic imagery with believable lighting, natural materials, grounded anatomy, and real-world texture fidelity. Do not drift into animation or illustration.',
  },
  {
    value: 'three_d_animated',
    label: '3D Animated',
    promptInstruction:
      'Style mandate: polished 3D animated feature look with dimensional characters, stylized but coherent materials, expressive lighting, and cinematic animation energy. Do not render this as live action or flat illustration.',
  },
  {
    value: 'hand_drawn',
    label: 'Hand Drawn',
    promptInstruction:
      'Style mandate: hand-drawn illustrated imagery with visible draftsmanship, painterly or inked mark-making, and intentionally illustrated surfaces. Do not make this look photoreal or CGI.',
  },
  {
    value: 'anime',
    label: 'Anime',
    promptInstruction:
      'Style mandate: premium anime-style visuals with strong silhouette design, expressive faces, stylized hair and eyes, clean line treatment, and high-end animated key-art energy. Do not drift into western comic or photoreal rendering.',
  },
  {
    value: 'graphic_novel',
    label: 'Graphic Novel',
    promptInstruction:
      'Style mandate: bold graphic-novel visuals with strong shape language, graphic contrast, deliberate panel-art composition, and stylized illustrative rendering. Avoid soft realism and generic animation looks.',
  },
  {
    value: 'storybook_illustration',
    label: 'Storybook Illustration',
    promptInstruction:
      'Style mandate: lush storybook illustration with elegant painterly detail, readable silhouettes, curated color harmony, and premium cover-art sensibility. Avoid photoreal or hard-edged CGI treatment.',
  },
  {
    value: 'custom',
    label: 'Custom',
    promptInstruction: 'Style mandate: follow the user-provided custom visual style direction exactly and keep it visually consistent throughout the clip.',
  },
];

const statusColor = (status?: string): string => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'failed') return 'error';
  if (normalized === 'running' || normalized === 'retrying') return 'processing';
  if (normalized === 'queued' || normalized === 'pending') return 'warning';
  return 'default';
};

const renderVariantLabel = (variant?: string | null): string | null => {
  const normalized = (variant || '').toLowerCase().trim();
  if (!normalized) return null;
  if (normalized === 'system_reversed_forward') return 'System Reversed';
  if (normalized === 'ai_original_backwards') return 'AI Original';
  if (normalized === 'final') return null;
  return normalized;
};

const formatVideoModeLabel = (mode?: string): string => {
  const normalized = String(mode || '').trim();
  if (!normalized) return 'Other';
  return normalized
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const resolveVideoPlaybackUrl = ({
  apiBase,
  bookId,
  userId,
  video,
}: {
  apiBase: string;
  bookId: string;
  userId: string;
  video: MarketingVideoRecord;
}): string | null => {
  if (video.latestHistoryId) {
    return buildHistoryStreamUrl({
      apiBase,
      bookId,
      historyId: video.latestHistoryId,
      userId,
    });
  }
  return video.signedUrl || null;
};

const formatVideoStatusLabel = (status?: string): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatVideoCardTimestamp = (video: MarketingVideoRecord): string | null => {
  const normalizedStatus = String(video.status || '').trim().toLowerCase();
  const isCompletedLike = normalizedStatus === 'completed' || normalizedStatus === 'failed';
  const rawTimestamp = isCompletedLike ? video.updatedAt || video.createdAt : video.createdAt || video.updatedAt;
  if (!rawTimestamp) return null;
  const parsed = new Date(rawTimestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  const label =
    normalizedStatus === 'completed'
      ? 'Completed'
      : normalizedStatus === 'failed'
        ? 'Finished'
        : 'Ran';
  return `${label} ${parsed.toLocaleString()}`;
};

const getAvailableCredits = (balance?: CreditBalance | null): number => {
  if (!balance) return 0;
  return Number(balance.available_balance ?? balance.credit_balance ?? 0);
};

const resolveCharacterAssetPreviewSrc = (asset?: MarketingCharacterAsset | null): string => {
  if (!asset) return '';
  return asset.previewUrl || asset.previewStreamUrl || asset.imageUrl || asset.imageStreamUrl || '';
};

const resolveCharacterAssetImageSrc = (asset?: MarketingCharacterAsset | null): string => {
  if (!asset) return '';
  return asset.imageUrl || asset.imageStreamUrl || asset.previewUrl || asset.previewStreamUrl || '';
};

const goalSupportsCharacterReferences = (goal: MarketingVideoSimpleGoalConfig | null | undefined): boolean =>
  Boolean(goal?.supportsCharacterReferences);

const resolveGenerationPasses = (mode?: string, generationPasses?: number): number => {
  if (typeof generationPasses === 'number' && Number.isFinite(generationPasses) && generationPasses > 0) {
    return Math.floor(generationPasses);
  }
  return (mode || '').toLowerCase() === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED ? 2 : 1;
};

const resolveFinalDurationSeconds = (
  durationSeconds?: number,
  mode?: string,
  generationPasses?: number,
  finalDurationSeconds?: number
): number | null => {
  if (
    typeof finalDurationSeconds === 'number' &&
    Number.isFinite(finalDurationSeconds) &&
    finalDurationSeconds > 0
  ) {
    return Math.floor(finalDurationSeconds);
  }
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }
  return Math.floor(durationSeconds) * resolveGenerationPasses(mode, generationPasses);
};

const resolveBilledDurationSeconds = (
  durationSeconds?: number,
  mode?: string,
  billedDurationSeconds?: number
): number | null => {
  if (
    typeof billedDurationSeconds === 'number' &&
    Number.isFinite(billedDurationSeconds) &&
    billedDurationSeconds > 0
  ) {
    return Math.floor(billedDurationSeconds);
  }
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }
  if ((mode || '').toLowerCase() === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED) {
    return Math.floor(durationSeconds) + COVER_ANIMATE_PRIMER_SECONDS;
  }
  return Math.floor(durationSeconds);
};

const aspectRatioFromResolution = (resolution: string, fallback = '16:9'): string => {
  const normalized = String(resolution || '').trim().toLowerCase().replace('×', 'x');
  if (!normalized.includes('x')) {
    return fallback;
  }
  const [widthText, heightText] = normalized.split('x', 2);
  const width = Number(widthText);
  const height = Number(heightText);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return fallback;
  }
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const divisor = gcd(Math.floor(width), Math.floor(height)) || 1;
  return `${Math.floor(width) / divisor}:${Math.floor(height) / divisor}`;
};

const describeAspectRatio = (aspectRatio: string): 'Landscape' | 'Portrait' | 'Square' | null => {
  const [widthText = '0', heightText = '0'] = String(aspectRatio || '').split(':', 2);
  const width = Number(widthText);
  const height = Number(heightText);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  if (width === height) return 'Square';
  return width > height ? 'Landscape' : 'Portrait';
};

const formatAspectRatioLabel = (aspectRatio: string): string => {
  const descriptor = describeAspectRatio(aspectRatio);
  return descriptor ? `${aspectRatio} (${descriptor})` : aspectRatio;
};

const getSelectableOutputOptions = (modelConfig: MarketingVideoModelCatalogItem | null): string[] => {
  if (!modelConfig) return [];
  const supportedSizes = modelConfig.supportedSizes || [];
  if (String(modelConfig.defaultResolution || '').includes('x') || supportedSizes.length > 0) {
    return supportedSizes.length > 0
      ? supportedSizes
      : (modelConfig.supportedResolutions || []).filter(option => String(option).includes('x'));
  }
  return modelConfig.supportedResolutions || [];
};

const getPreferredOutputOption = (
  modelConfig: MarketingVideoModelCatalogItem | null,
  currentSelection?: string | null
): string => {
  const outputOptions = getSelectableOutputOptions(modelConfig);
  if (currentSelection && outputOptions.includes(currentSelection)) {
    return currentSelection;
  }
  const defaultResolution = String(modelConfig?.defaultResolution || '');
  if (defaultResolution && outputOptions.includes(defaultResolution)) {
    return defaultResolution;
  }
  return outputOptions[0] || defaultResolution || '';
};

const resolutionToDimensions = (resolution: string, aspectRatio: string): { width: number; height: number } | null => {
  const normalized = String(resolution || '').trim().toLowerCase().replace('×', 'x');
  if (normalized.includes('x')) {
    const [widthText, heightText] = normalized.split('x', 2);
    const width = Number(widthText);
    const height = Number(heightText);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
    return null;
  }

  const [ratioWidthText = '16', ratioHeightText = '9'] = String(aspectRatio || '16:9').split(':', 2);
  const ratioWidth = Number(ratioWidthText);
  const ratioHeight = Number(ratioHeightText);
  if (!Number.isFinite(ratioWidth) || !Number.isFinite(ratioHeight) || ratioWidth <= 0 || ratioHeight <= 0) {
    return null;
  }

  let height = 0;
  if (normalized === '720p') height = 720;
  if (normalized === '1080p') height = 1080;
  if (normalized === '4k') height = 2160;
  if (!height) return null;

  let width = Math.round(height * (ratioWidth / ratioHeight));
  if (width % 2 === 1) width += 1;
  if (height % 2 === 1) height += 1;
  return { width, height };
};

const estimatePricing = ({
  model,
  resolution,
  aspectRatio,
  durationSeconds,
  sampleCount,
  generateAudio,
  mode,
}: {
  model: MarketingVideoModelCatalogItem | null;
  resolution: string;
  aspectRatio: string;
  durationSeconds: number;
  sampleCount: number;
  generateAudio: boolean;
  mode: string;
}) => {
  const finalDurationSeconds = Math.max(1, durationSeconds || 1);
  const generationPasses = mode === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED ? 2 : 1;
  const primerDurationSeconds =
    mode === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED ? COVER_ANIMATE_PRIMER_SECONDS : 0;
  const billedDurationSeconds = finalDurationSeconds + primerDurationSeconds;
  const base = {
    usdPerSecond: 0,
    estimatedTokens: null as number | null,
    estimatedUsd: 0,
    estimatedCredits: 0,
    generationPasses,
    primerDurationSeconds,
    billedDurationSeconds,
    finalDurationSeconds,
    pricingKind: model?.pricingKind || 'per_second',
  };

  if (!model) {
    return base;
  }

  const pricingConfig = model.pricingConfig || {};
  const usdPerCredit = USD_PER_CREDIT_FALLBACK;

  if (model.pricingKind === 'token_formula') {
    const dims = resolutionToDimensions(resolution, aspectRatio);
    const fps = Number(pricingConfig.fps || 24);
    const divisor = Number(pricingConfig.divisor || 1024);
    const usdPerMillionTokens = Number(pricingConfig.usdPerMillionTokens || 0);
    const bufferMultiplier = Number(pricingConfig.bufferMultiplier || 1);
    const tokensPerSample =
      dims && usdPerMillionTokens > 0
        ? (dims.width * dims.height * billedDurationSeconds * fps) / divisor
        : 0;
    const estimatedUsd =
      tokensPerSample > 0
        ? ((tokensPerSample / 1_000_000) * usdPerMillionTokens * bufferMultiplier * Math.max(1, sampleCount))
        : 0;
    return {
      ...base,
      estimatedTokens: tokensPerSample > 0 ? tokensPerSample * Math.max(1, sampleCount) : null,
      estimatedUsd,
      estimatedCredits: Math.ceil(estimatedUsd / usdPerCredit),
    };
  }

  const ratesByOutput = pricingConfig.ratesByOutput || {};
  const rateConfig = ratesByOutput[resolution] || {};
  const usdPerSecond = generateAudio
    ? Number(rateConfig.audioOn ?? pricingConfig.defaultAudioOn ?? 0)
    : Number(rateConfig.audioOff ?? pricingConfig.defaultAudioOff ?? 0);
  const estimatedUsd = billedDurationSeconds * Math.max(1, sampleCount) * usdPerSecond;
  return {
    ...base,
    usdPerSecond,
    estimatedUsd,
    estimatedCredits: Math.ceil(estimatedUsd / usdPerCredit),
  };
};

const formatOutputSizeLabel = (size: string): string => {
  const ratio = aspectRatioFromResolution(size, '?');
  const [widthText = '0', heightText = '0'] = size.split('x');
  const width = Number(widthText);
  const height = Number(heightText);
  let descriptor: string | null = null;
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    if (width === height) descriptor = 'Square';
    else descriptor = width > height ? 'Landscape' : 'Portrait';
  }
  return descriptor ? `${size} (${ratio}, ${descriptor})` : `${size} (${ratio})`;
};

const copyText = async (value: string, successMessage: string) => {
  if (!value.trim()) return;
  await navigator.clipboard.writeText(value.trim());
  message.success(successMessage);
};

const buildHistoryStreamUrl = ({
  apiBase,
  bookId,
  historyId,
  userId,
  download = false,
  filename,
}: {
  apiBase: string;
  bookId: string;
  historyId: string;
  userId: string;
  download?: boolean;
  filename?: string;
}) => {
  const params = new URLSearchParams();
  params.set('userId', userId);
  if (download) params.set('download', '1');
  if (filename) params.set('filename', filename);
  return `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history/${encodeURIComponent(historyId)}/stream?${params.toString()}`;
};

const getGoalConfig = (
  goals: MarketingVideoSimpleGoalConfig[] | undefined,
  goalId: MarketingVideoSimpleGoal
): MarketingVideoSimpleGoalConfig | null => goals?.find(goal => goal.id === goalId) || goals?.[0] || null;

const getOrientationLabel = (goal: MarketingVideoSimpleGoalConfig): string => {
  const matchingOrientation = goal.orientations.find(orientation => orientation.id === goal.defaultOrientationId);
  return matchingOrientation?.label || describeAspectRatio(goal.defaultAspectRatio) || goal.defaultOrientationId;
};

const getPresetSuggestionLabel = (goal: MarketingVideoSimpleGoalConfig): string =>
  `Suggested: ${getOrientationLabel(goal)} (${goal.defaultAspectRatio})`;

const PRESET_COLLECTION_LABELS: Record<PresetCollectionFilter, string> = {
  featured: 'Featured',
  all: 'All Presets',
  general: 'General',
  romance: 'Romance',
  mystery: 'Mystery',
  thriller: 'Thriller',
  fantasy: 'Fantasy',
  science_fiction: 'Science Fiction',
  horror: 'Horror',
  historical: 'Historical',
  paranormal: 'Paranormal',
  young_adult: 'Young Adult',
};

const PRESET_FAMILY_DISPLAY_ORDER: MarketingVideoPresetFamily[] = [
  'general',
  'romance',
  'mystery',
  'thriller',
  'fantasy',
  'science_fiction',
  'horror',
  'historical',
  'paranormal',
  'young_adult',
];

const normalizePresetFamily = (goal: MarketingVideoSimpleGoalConfig): MarketingVideoPresetFamily =>
  goal.presetFamily || 'general';

const getVisibleGoals = (
  goals: MarketingVideoSimpleGoalConfig[] | undefined,
  filter: PresetCollectionFilter
): MarketingVideoSimpleGoalConfig[] => {
  const catalog = goals || [];
  if (!catalog.length) return [];
  if (filter === 'all') return catalog;
  if (filter === 'featured') {
    const featuredGoals = catalog.filter(goal => goal.featured !== false);
    return featuredGoals.length ? featuredGoals : catalog;
  }
  return catalog.filter(goal => normalizePresetFamily(goal) === filter);
};

const buildPresetSearchText = (goal: MarketingVideoSimpleGoalConfig): string =>
  [
    goal.label,
    goal.description,
    getPresetSuggestionLabel(goal),
    PRESET_COLLECTION_LABELS[normalizePresetFamily(goal)],
    ...(goal.searchTerms || []),
  ]
    .join(' ')
    .toLowerCase();

const getOutputArea = (outputOption: string, aspectRatio: string): number => {
  const dims = resolutionToDimensions(outputOption, aspectRatio);
  if (!dims) return Number.MAX_SAFE_INTEGER;
  return dims.width * dims.height;
};

const getLowestQualityOutputOption = (
  modelConfig: MarketingVideoModelCatalogItem | null,
  aspectRatio: string
): string => {
  const outputOptions = getSelectableOutputOptions(modelConfig);
  if (!outputOptions.length) {
    return getPreferredOutputOption(modelConfig, null);
  }
  const usesExactSizes =
    String(modelConfig?.defaultResolution || '').includes('x') || Boolean(modelConfig?.supportedSizes?.length);
  const matchingOptions = usesExactSizes
    ? outputOptions.filter(option => aspectRatioFromResolution(option, '') === aspectRatio)
    : outputOptions;
  const candidateOptions = matchingOptions.length ? matchingOptions : outputOptions;
  return [...candidateOptions].sort((left, right) => getOutputArea(left, aspectRatio) - getOutputArea(right, aspectRatio))[0];
};

const buildPresetOptionLabel = (goal: MarketingVideoSimpleGoalConfig) => (
  <Space direction="vertical" size={0}>
    <span>{goal.label}</span>
    <Text type="secondary" style={{ fontSize: 12 }}>
      {getPresetSuggestionLabel(goal)}
    </Text>
  </Space>
);

const getVideoStyleInstruction = (styleId: MarketingVideoStyleId, customStyleText?: string): string => {
  if (styleId === 'custom') {
    const custom = String(customStyleText || '').trim();
    if (!custom) {
      return 'Style mandate: follow the user-provided custom visual style direction exactly and keep it visually consistent throughout the clip.';
    }
    return `Style mandate: ${custom}. Keep this custom visual style unmistakable and consistent throughout the clip.`;
  }
  return (
    VIDEO_STYLE_OPTIONS.find(option => option.value === styleId)?.promptInstruction ||
    VIDEO_STYLE_OPTIONS.find(option => option.value === DEFAULT_VIDEO_STYLE)?.promptInstruction ||
    ''
  );
};

const getVideoStyleLabel = (styleId: MarketingVideoStyleId, customStyleText?: string): string => {
  if (styleId === 'custom') {
    const custom = String(customStyleText || '').trim();
    return custom ? `Custom: ${custom}` : 'Custom';
  }
  return VIDEO_STYLE_OPTIONS.find(option => option.value === styleId)?.label || styleId;
};

const buildCharacterDescriptionFallbackLines = (assets: MarketingCharacterAsset[]): string[] => {
  const deduped = new Map<string, string>();
  assets.forEach(asset => {
    const characterId = String(asset.characterId || '').trim().toLowerCase();
    const characterName = String(asset.characterName || '').trim();
    const descriptionSummary = String(asset.descriptionSummary || '').trim();
    const dedupeKey = characterId || characterName.toLowerCase() || descriptionSummary.toLowerCase();
    if (!dedupeKey || deduped.has(dedupeKey)) return;
    if (characterName && descriptionSummary) {
      deduped.set(dedupeKey, `${characterName}: ${descriptionSummary}`);
      return;
    }
    if (descriptionSummary) {
      deduped.set(dedupeKey, descriptionSummary);
      return;
    }
    if (characterName) {
      deduped.set(dedupeKey, `${characterName}: preserve this character's identity consistently across the clip.`);
    }
  });
  return [...deduped.values()];
};

const appendCharacterDescriptionFallbackToPrompt = (prompt: string, fallbackLines: string[]): string => {
  const normalizedPrompt = String(prompt || '').trim();
  if (!fallbackLines.length || normalizedPrompt.includes(CHARACTER_DESCRIPTION_FALLBACK_HEADING)) {
    return normalizedPrompt;
  }
  return [normalizedPrompt, CHARACTER_DESCRIPTION_FALLBACK_HEADING, ...fallbackLines.map(line => `- ${line}`)]
    .filter(Boolean)
    .join('\n');
};

const composeMarketingVideoPrompt = ({
  basePrompt,
  inspirationContext,
  styleId,
  customStyleText,
  usesCoverReference,
  coverReferenceRequired,
  characterReferenceNames,
  characterDescriptionFallbackLines,
}: {
  basePrompt: string;
  inspirationContext?: string;
  styleId: MarketingVideoStyleId;
  customStyleText?: string;
  usesCoverReference: boolean;
  coverReferenceRequired: boolean;
  characterReferenceNames: string[];
  characterDescriptionFallbackLines: string[];
}): string => {
  const prompt = String(basePrompt || '').trim();
  const directives = [
    inspirationContext ? inspirationContext.trim() : '',
    getVideoStyleInstruction(styleId, customStyleText),
  ];
  if (coverReferenceRequired) {
    directives.push(
      'Reference directive: use the official cover only as the exact ending-frame target. Begin with a distinct moving scene, preserve visual continuity as it approaches the cover, and do not use the cover as the opening frame.'
    );
  } else if (usesCoverReference) {
    directives.push(
      'Reference directive: use the official cover as a strong visual anchor for style, motif continuity, subject identity, and worldbuilding cues.'
    );
  } else {
    directives.push(
      'Reference directive: do not rely on the cover image. Build the visual world entirely from the written prompt while keeping the result marketable and coherent.'
    );
  }
  if (characterReferenceNames.length) {
    directives.push(
      `Character continuity directive: preserve the supplied portrait-reference identity for ${characterReferenceNames.join(', ')}. Keep face shape, hair, age presentation, and recognizable design traits consistent.`
    );
  }
  return appendCharacterDescriptionFallbackToPrompt(
    [prompt, ...directives.filter(Boolean)].filter(Boolean).join('\n\n'),
    characterDescriptionFallbackLines
  );
};

const resolvePromptConditioningMode = ({
  goal,
  modelConfig,
  hasCoverReference,
  hasCharacterReferences,
  allowCharacterReferenceTemplates,
}: {
  goal: MarketingVideoSimpleGoalConfig | null;
  modelConfig: MarketingVideoModelCatalogItem | null;
  hasCoverReference: boolean;
  hasCharacterReferences: boolean;
  allowCharacterReferenceTemplates: boolean;
}): PromptConditioningMode => {
  if (!goal || !modelConfig) return 'textOnly';
  const canUseCharacterReferenceTemplates =
    allowCharacterReferenceTemplates &&
    hasCharacterReferences &&
    goalSupportsCharacterReferences(goal) &&
    goal.mode !== 'cover_animate' &&
    goal.mode !== 'backwards' &&
    modelConfig.supportsReferenceImages;
  if (canUseCharacterReferenceTemplates && hasCoverReference) {
    return 'coverAndCharacterReference';
  }
  if (canUseCharacterReferenceTemplates) {
    return 'characterReference';
  }
  if (!hasCoverReference) return 'textOnly';
  if (goal.mode === 'cover_animate' && modelConfig.supportsLastFrameImage) {
    return 'lastFrame';
  }
  if (
    goal.mode === 'backwards' &&
    modelConfig.supportsFirstFrameImage
  ) {
    return 'firstFrame';
  }
  if (goal.mode !== 'cover_animate' && goal.mode !== 'backwards' && modelConfig.supportsReferenceImages) {
    return 'referenceImage';
  }
  return 'textOnly';
};

const getRecommendedPrompt = ({
  goal,
  modelConfig,
  hasCoverReference,
  hasCharacterReferences,
  allowCharacterReferenceTemplates,
  inspirationContext,
  styleId,
  customStyleText,
  coverReferenceRequired,
  characterReferenceNames,
  characterDescriptionFallbackLines,
}: {
  goal: MarketingVideoSimpleGoalConfig | null;
  modelConfig: MarketingVideoModelCatalogItem | null;
  hasCoverReference: boolean;
  hasCharacterReferences: boolean;
  allowCharacterReferenceTemplates: boolean;
  inspirationContext?: string;
  styleId: MarketingVideoStyleId;
  customStyleText?: string;
  coverReferenceRequired: boolean;
  characterReferenceNames: string[];
  characterDescriptionFallbackLines: string[];
}): string => {
  if (!goal) return '';
  const conditioningMode = resolvePromptConditioningMode({
    goal,
    modelConfig,
    hasCoverReference,
    hasCharacterReferences,
    allowCharacterReferenceTemplates,
  });
  const templates: MarketingVideoPromptTemplates = goal.promptTemplates || { textOnly: '' };
  let basePrompt = templates.textOnly;
  if (conditioningMode === 'lastFrame') {
    basePrompt = templates.lastFrame || templates.firstAndLastFrame || templates.referenceImage || templates.textOnly;
  } else if (conditioningMode === 'firstAndLastFrame') {
    basePrompt = templates.firstAndLastFrame || templates.firstFrame || templates.referenceImage || templates.textOnly;
  } else if (conditioningMode === 'firstFrame') {
    basePrompt = templates.firstFrame || templates.referenceImage || templates.textOnly;
  } else if (conditioningMode === 'coverAndCharacterReference') {
    basePrompt =
      templates.coverAndCharacterReference ||
      templates.characterReference ||
      templates.referenceImage ||
      templates.textOnly;
  } else if (conditioningMode === 'characterReference') {
    basePrompt = templates.characterReference || templates.referenceImage || templates.textOnly;
  } else if (conditioningMode === 'referenceImage') {
    basePrompt = templates.referenceImage || templates.textOnly;
  }
  return composeMarketingVideoPrompt({
    basePrompt,
    inspirationContext,
    styleId,
    customStyleText,
    usesCoverReference: hasCoverReference,
    coverReferenceRequired,
    characterReferenceNames,
    characterDescriptionFallbackLines,
  });
};

const MarketingVideoModule: React.FC<MarketingVideoModuleProps> = ({
  apiBase,
  bookId,
  userId,
  bookTitle,
  sourceLanguage,
  selectedLanguage,
  actionSignal,
  creditBalance,
  fetchCreditBalance,
  initialStudioView = 'create',
  allowedStudioViews = ['create', 'library'],
  libraryTitle = 'Created Videos',
  initialSimpleGoalId,
  initialGoalRequestId,
  inspirationRefreshSignal,
  onOpenSourceKit,
}) => {
  const navigate = useNavigate();
  const allowedStudioViewsKey = useMemo(
    () => (allowedStudioViews.length ? [...allowedStudioViews].join('|') : 'create|library'),
    [allowedStudioViews]
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingSimpleConfig, setIsLoadingSimpleConfig] = useState(false);
  const [isLoadingCharacterAssets, setIsLoadingCharacterAssets] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [videos, setVideos] = useState<MarketingVideoRecord[]>([]);
  const [modelCatalog, setModelCatalog] = useState<MarketingVideoModelCatalogItem[]>([]);
  const [simpleConfig, setSimpleConfig] = useState<MarketingVideoSimpleConfig | null>(null);
  const [characterAssets, setCharacterAssets] = useState<MarketingCharacterAsset[]>([]);
  const [inspirationItems, setInspirationItems] = useState<MarketingInspirationItem[]>([]);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [selectedInspirationIds, setSelectedInspirationIds] = useState<string[]>([]);
  const [selectedCharacterReferenceIds, setSelectedCharacterReferenceIds] = useState<string[]>([]);
  const [characterReferenceModalOpen, setCharacterReferenceModalOpen] = useState(false);
  const [characterReferenceSearch, setCharacterReferenceSearch] = useState('');
  const [characterReferenceCharacterFilter, setCharacterReferenceCharacterFilter] = useState<string>('all');
  const [uiMode, setUiMode] = useState<MarketingVideoUiMode>(() => {
    if (typeof window === 'undefined') return 'simple';
    return window.localStorage.getItem(VIDEO_STUDIO_UI_MODE_STORAGE_KEY) === 'advanced'
      ? 'advanced'
      : 'simple';
  });
  const [videoStyle, setVideoStyle] = useState<MarketingVideoStyleId>(DEFAULT_VIDEO_STYLE);
  const [customVideoStyle, setCustomVideoStyle] = useState<string>('');
  const [useCoverReference, setUseCoverReference] = useState<boolean>(true);
  const resolvedAllowedStudioViews = useMemo<MarketingStudioView[]>(
    () => (allowedStudioViews.length ? allowedStudioViews : ['create', 'library']),
    [allowedStudioViewsKey]
  );
  const createViewAllowed = useMemo(
    () => resolvedAllowedStudioViews.includes('create'),
    [resolvedAllowedStudioViews]
  );
  const [studioView, setStudioView] = useState<MarketingStudioView>(
    resolvedAllowedStudioViews.includes(initialStudioView) ? initialStudioView : resolvedAllowedStudioViews[0]
  );
  const [advancedDetailsOpen, setAdvancedDetailsOpen] = useState(false);

  const [mode, setMode] = useState<string>('cover_animate');
  const [model, setModel] = useState<string>('');
  const [aspectRatio, setAspectRatio] = useState<string>('9:16');
  const [selectedResolution, setSelectedResolution] = useState<string>('');
  const [durationSeconds, setDurationSeconds] = useState<number>(8);
  const [sampleCount, setSampleCount] = useState<number>(1);
  const [generateAudio, setGenerateAudio] = useState<boolean>(true);
  const [prompt, setPrompt] = useState<string>('');
  const [promptDirty, setPromptDirty] = useState<boolean>(false);
  const [negativePrompt, setNegativePrompt] = useState<string>('');
  const [seed, setSeed] = useState<number | null>(null);

  const [simpleGoalId, setSimpleGoalId] = useState<MarketingVideoSimpleGoal>('animated_cover');
  const [advancedGoalId, setAdvancedGoalId] = useState<MarketingVideoSimpleGoal>('animated_cover');
  const [presetCollectionFilter, setPresetCollectionFilter] = useState<PresetCollectionFilter>('featured');
  const [simpleOrientationId, setSimpleOrientationId] = useState<'landscape' | 'portrait' | 'square'>('portrait');
  const [simpleLengthSeconds, setSimpleLengthSeconds] = useState<number>(8);
  const [simpleGenerateAudio, setSimpleGenerateAudio] = useState<boolean>(true);
  const [videoLibrarySearch, setVideoLibrarySearch] = useState<string>('');
  const [videoLibraryModeFilter, setVideoLibraryModeFilter] = useState<string>('all');
  const [videoLibraryStatusFilter, setVideoLibraryStatusFilter] = useState<string>('all');
  const [videoLibraryEditionScope, setVideoLibraryEditionScope] = useState<'current' | 'all'>('current');
  const [selectedLibraryVideoId, setSelectedLibraryVideoId] = useState<string>('');
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [videoPlayerLoading, setVideoPlayerLoading] = useState(false);
  const [videoCostBreakdownOpen, setVideoCostBreakdownOpen] = useState(false);

  const lastProcessedActionTsRef = useRef<number | null>(null);
  const lastInspirationRefreshSignalRef = useRef(inspirationRefreshSignal);
  const promptInitializedRef = useRef(false);
  const appliedInitialGoalRequestRef = useRef<number | null>(null);
  const appliedInitialSourceRequestRef = useRef<number | null>(null);
  const seenVideoStatusesRef = useRef<Map<string, string> | null>(null);
  const availableCredits = useMemo(() => getAvailableCredits(creditBalance), [creditBalance]);
  const resolvedLanguage = useMemo(
    () => (selectedLanguage || 'Original').trim() || 'Original',
    [selectedLanguage]
  );
  const screens = Grid.useBreakpoint();
  const isDesktopCreateLayout = Boolean(screens.lg);

  const selectedModelConfig = useMemo(
    () => modelCatalog.find(option => option.id === model) || modelCatalog[0] || null,
    [model, modelCatalog]
  );
  const durationOptions = selectedModelConfig?.supportedDurations || [];
  const usesSizeOptions = useMemo(() => {
    if (!selectedModelConfig) return false;
    return (
      String(selectedModelConfig.defaultResolution || '').includes('x') ||
      (selectedModelConfig.supportedSizes || []).length > 0
    );
  }, [selectedModelConfig]);
  const selectableOutputOptions = useMemo(
    () => getSelectableOutputOptions(selectedModelConfig),
    [selectedModelConfig]
  );
  const resolution = useMemo(
    () => getPreferredOutputOption(selectedModelConfig, selectedResolution),
    [selectedModelConfig, selectedResolution]
  );
  const effectiveAspectRatio = useMemo(() => {
    if (usesSizeOptions) {
      return aspectRatioFromResolution(resolution, aspectRatio);
    }
    return aspectRatio;
  }, [aspectRatio, usesSizeOptions, resolution]);
  const effectiveGenerateAudio = useMemo(() => {
    if (!selectedModelConfig?.supportsAudio) return false;
    if (!selectedModelConfig.audioOptional) return true;
    return generateAudio;
  }, [generateAudio, selectedModelConfig]);

  const selectedSimpleGoal = useMemo(
    () => simpleConfig?.goals.find(goal => goal.id === simpleGoalId) || simpleConfig?.goals[0] || null,
    [simpleConfig, simpleGoalId]
  );
  const visibleGoalOptions = useMemo(() => {
    const visibleGoals = getVisibleGoals(simpleConfig?.goals, presetCollectionFilter);
    return visibleGoals.length ? visibleGoals : simpleConfig?.goals || [];
  }, [presetCollectionFilter, simpleConfig?.goals]);
  const presetCollectionOptions = useMemo(() => {
    const availableFamilies = new Set(
      (simpleConfig?.goals || []).map(goal => normalizePresetFamily(goal))
    );
    return [
      { label: PRESET_COLLECTION_LABELS.featured, value: 'featured' as const },
      { label: PRESET_COLLECTION_LABELS.all, value: 'all' as const },
      ...PRESET_FAMILY_DISPLAY_ORDER.filter(family => availableFamilies.has(family)).map(family => ({
        label: PRESET_COLLECTION_LABELS[family],
        value: family,
      })),
    ];
  }, [simpleConfig?.goals]);
  const selectedAdvancedGoal = useMemo(
    () => getGoalConfig(simpleConfig?.goals, advancedGoalId) || selectedSimpleGoal,
    [advancedGoalId, selectedSimpleGoal, simpleConfig?.goals]
  );
  const selectedSimpleOrientation = useMemo(() => {
    if (!selectedSimpleGoal) return null;
    return (
      selectedSimpleGoal.orientations.find(orientation => orientation.id === simpleOrientationId) ||
      selectedSimpleGoal.orientations[0] ||
      null
    );
  }, [selectedSimpleGoal, simpleOrientationId]);
  const selectedSimpleLengthOption = useMemo(() => {
    if (!selectedSimpleOrientation) return null;
    return (
      selectedSimpleOrientation.lengths.find(option => option.seconds === simpleLengthSeconds) ||
      selectedSimpleOrientation.lengths[0] ||
      null
    );
  }, [selectedSimpleOrientation, simpleLengthSeconds]);
  const selectedSimpleModelConfig = useMemo(
    () =>
      modelCatalog.find(option => option.id === selectedSimpleLengthOption?.resolvedModelId) || null,
    [modelCatalog, selectedSimpleLengthOption]
  );
  const selectedCharacterReferenceAssets = useMemo(
    () =>
      selectedCharacterReferenceIds
        .map(assetId => characterAssets.find(asset => asset.id === assetId) || null)
        .filter((asset): asset is MarketingCharacterAsset => Boolean(asset)),
    [characterAssets, selectedCharacterReferenceIds]
  );
  const selectedInspirationItems = useMemo(
    () =>
      sortMarketingInspirationItems(
        selectedInspirationIds
          .map(itemId => inspirationItems.find(item => item.id === itemId) || null)
          .filter((item): item is MarketingInspirationItem => Boolean(item))
      ),
    [inspirationItems, selectedInspirationIds]
  );
  const approvedInspirationCount = useMemo(
    () => inspirationItems.filter(item => String(item.status || '').toLowerCase() === 'approved').length,
    [inspirationItems]
  );
  const inspirationContext = useMemo(
    () => buildMarketingInspirationContext(selectedInspirationItems),
    [selectedInspirationItems]
  );
  const groupedCharacterAssets = useMemo(() => {
    const groups = new Map<string, { characterId: string; characterName: string; items: MarketingCharacterAsset[] }>();
    characterAssets.forEach(asset => {
      const characterId = String(asset.characterId || asset.characterName || asset.id || '').trim();
      const characterName = String(asset.characterName || 'Character').trim() || 'Character';
      const existing = groups.get(characterId);
      if (existing) {
        existing.items.push(asset);
        return;
      }
      groups.set(characterId, { characterId, characterName, items: [asset] });
    });
    return [...groups.values()].sort((left, right) => left.characterName.localeCompare(right.characterName));
  }, [characterAssets]);
  const characterReferenceCharacterOptions = useMemo(
    () => [
      { label: 'All Characters', value: 'all' },
      ...groupedCharacterAssets.map(group => ({
        label: `${group.characterName} (${group.items.length})`,
        value: group.characterId,
      })),
    ],
    [groupedCharacterAssets]
  );
  const filteredCharacterAssetGroups = useMemo(() => {
    const normalizedSearch = characterReferenceSearch.trim().toLowerCase();
    return groupedCharacterAssets
      .filter(group => characterReferenceCharacterFilter === 'all' || group.characterId === characterReferenceCharacterFilter)
      .map(group => ({
        ...group,
        items: group.items.filter(asset => {
          if (!normalizedSearch) return true;
          const haystack = [
            asset.characterName,
            asset.stylePreset,
            asset.prompt,
            asset.aspectRatio,
            asset.size,
          ]
            .map(value => String(value || '').toLowerCase())
            .join(' ');
          return haystack.includes(normalizedSearch);
        }),
      }))
      .filter(group => group.items.length > 0);
  }, [characterReferenceCharacterFilter, characterReferenceSearch, groupedCharacterAssets]);
  const currentSimpleGoalSupportsCharacterReferences = goalSupportsCharacterReferences(selectedSimpleGoal);
  const hasAvailableCoverReference = Boolean(simpleConfig?.hasCoverReference);
  const coverReferenceRequired = useMemo(() => {
    if (uiMode === 'simple') {
      return Boolean(selectedSimpleGoal?.requiresCoverReference);
    }
    return mode === 'cover_animate';
  }, [mode, selectedSimpleGoal?.requiresCoverReference, uiMode]);
  const effectiveUseCoverReference = hasAvailableCoverReference && (coverReferenceRequired || useCoverReference);
  const canApplyCharacterReferences = uiMode === 'advanced' || currentSimpleGoalSupportsCharacterReferences;
  const activeModelConfig = uiMode === 'simple' ? selectedSimpleModelConfig : selectedModelConfig;
  const activeModelSupportsReferenceImages = Boolean(activeModelConfig?.supportsReferenceImages);
  const shouldUseCharacterReferenceImages = canApplyCharacterReferences && activeModelSupportsReferenceImages;
  const activeCharacterReferenceAssets = useMemo(
    () => (shouldUseCharacterReferenceImages ? selectedCharacterReferenceAssets : []),
    [selectedCharacterReferenceAssets, shouldUseCharacterReferenceImages]
  );
  const selectedCharacterReferenceNames = useMemo(
    () =>
      [
        ...new Set(
          selectedCharacterReferenceAssets.map(asset => String(asset.characterName || '').trim()).filter(Boolean)
        ),
      ],
    [selectedCharacterReferenceAssets]
  );
  const characterDescriptionFallbackLines = useMemo(() => {
    if (!canApplyCharacterReferences || activeModelSupportsReferenceImages) return [];
    return buildCharacterDescriptionFallbackLines(selectedCharacterReferenceAssets);
  }, [activeModelSupportsReferenceImages, canApplyCharacterReferences, selectedCharacterReferenceAssets]);
  const activeCharacterReferenceNames = useMemo(
    () => (canApplyCharacterReferences ? selectedCharacterReferenceNames : []),
    [canApplyCharacterReferences, selectedCharacterReferenceNames]
  );
  const getCurrentRecommendedPrompt = useCallback(
    ({
      goal,
      modelConfig,
      hasCharacterReferences,
      allowCharacterReferenceTemplates,
    }: {
      goal: MarketingVideoSimpleGoalConfig | null;
      modelConfig: MarketingVideoModelCatalogItem | null;
      hasCharacterReferences: boolean;
      allowCharacterReferenceTemplates: boolean;
    }) =>
      getRecommendedPrompt({
        goal,
        modelConfig,
        hasCoverReference: effectiveUseCoverReference,
        hasCharacterReferences,
        allowCharacterReferenceTemplates,
        inspirationContext,
        styleId: videoStyle,
        customStyleText: customVideoStyle,
        coverReferenceRequired,
        characterReferenceNames: activeCharacterReferenceNames,
        characterDescriptionFallbackLines,
      }),
    [
      activeCharacterReferenceNames,
      characterDescriptionFallbackLines,
      coverReferenceRequired,
      customVideoStyle,
      effectiveUseCoverReference,
      inspirationContext,
      videoStyle,
    ]
  );
  const effectiveSimpleGenerateAudio = useMemo(() => {
    if (!selectedSimpleLengthOption?.audioSupported) return false;
    if (!selectedSimpleLengthOption.audioOptional) return selectedSimpleLengthOption.defaultGenerateAudio;
    return simpleGenerateAudio;
  }, [selectedSimpleLengthOption, simpleGenerateAudio]);
  const activeImageReferenceSupportMessage = useMemo(() => {
    if (!activeModelConfig || activeModelConfig.supportsReferenceImages) return null;
    if (canApplyCharacterReferences && selectedCharacterReferenceAssets.length) {
      return `${activeModelConfig.displayName} does not support extra image references in this integration. Selected portraits will be converted into written character guidance instead.`;
    }
    if (activeModelConfig.supportsFrameImages) {
      return `${activeModelConfig.displayName} is limited to cover or frame conditioning in this integration. Extra image references such as character portraits are not supported.`;
    }
    return `${activeModelConfig.displayName} does not support extra image references in this integration. Use prompt-only guidance or switch to a reference-friendly model.`;
  }, [activeModelConfig, canApplyCharacterReferences, selectedCharacterReferenceAssets.length]);
  const coverReferenceRequirementMessage = useMemo(() => {
    if (!coverReferenceRequired) return null;
    if (hasAvailableCoverReference) return null;
    return 'This mode requires an existing book cover. Generate or upload a cover before running this video request.';
  }, [coverReferenceRequired, hasAvailableCoverReference]);
  const coverEndFrameSupportMessage = useMemo(() => {
    if (!coverReferenceRequired || !hasAvailableCoverReference || activeModelConfig?.supportsLastFrameImage) return null;
    return `${activeModelConfig?.displayName || 'This model'} cannot use the cover as an exact ending frame. Choose a model with last-frame support.`;
  }, [activeModelConfig, coverReferenceRequired, hasAvailableCoverReference]);
  const simplePricingEstimate = useMemo(() => {
    if (!selectedSimpleLengthOption) {
      return {
        estimatedCredits: 0,
        estimatedUsd: 0,
        estimatedTokens: null as number | null,
        billedDurationSeconds: 0,
        finalDurationSeconds: 0,
        generationPasses: 1,
        primerDurationSeconds: 0,
        pricingKind: 'per_second' as const,
        usdPerSecond: 0,
      };
    }
    return estimatePricing({
      model: selectedSimpleModelConfig,
      resolution: selectedSimpleLengthOption.resolvedRequest.resolution,
      aspectRatio: selectedSimpleLengthOption.resolvedRequest.aspectRatio,
      durationSeconds: selectedSimpleLengthOption.resolvedRequest.durationSeconds,
      sampleCount: 1,
      generateAudio: effectiveSimpleGenerateAudio,
      mode: selectedSimpleLengthOption.resolvedRequest.mode,
    });
  }, [effectiveSimpleGenerateAudio, selectedSimpleLengthOption, selectedSimpleModelConfig]);

  const advancedPricingEstimate = useMemo(() => {
    return estimatePricing({
      model: selectedModelConfig,
      resolution,
      aspectRatio: effectiveAspectRatio,
      durationSeconds,
      sampleCount,
      generateAudio: effectiveGenerateAudio,
      mode,
    });
  }, [durationSeconds, effectiveAspectRatio, effectiveGenerateAudio, mode, resolution, sampleCount, selectedModelConfig]);

  const activePricingEstimate = uiMode === 'simple' ? simplePricingEstimate : advancedPricingEstimate;
  const hasEnoughCredits = availableCredits >= activePricingEstimate.estimatedCredits;
  const videoModeFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const items = videos
      .map(video => {
        const value = String(video.mode || 'other').trim() || 'other';
        return { value, label: formatVideoModeLabel(video.mode) };
      })
      .filter(option => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      })
      .sort((left, right) => left.label.localeCompare(right.label));
    return [{ value: 'all', label: 'All Modes' }, ...items];
  }, [videos]);
  const videoStatusFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const items = videos
      .map(video => {
        const value = String(video.status || 'unknown').trim().toLowerCase() || 'unknown';
        return { value, label: formatVideoStatusLabel(video.status) };
      })
      .filter(option => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      })
      .sort((left, right) => left.label.localeCompare(right.label));
    return [{ value: 'all', label: 'All Statuses' }, ...items];
  }, [videos]);
  const filteredVideos = useMemo(() => {
    const normalizedSearch = videoLibrarySearch.trim().toLowerCase();
    return videos.filter(video => {
      if (videoLibraryModeFilter !== 'all') {
        const modeValue = String(video.mode || 'other').trim() || 'other';
        if (modeValue !== videoLibraryModeFilter) return false;
      }
      if (videoLibraryStatusFilter !== 'all') {
        const statusValue = String(video.status || 'unknown').trim().toLowerCase() || 'unknown';
        if (statusValue !== videoLibraryStatusFilter) return false;
      }
      if (!normalizedSearch) return true;
      const haystack = [
        video.prompt,
        video.model,
        video.mode,
        video.status,
        video.uiMode,
        video.aspectRatio,
        video.resolution,
        renderVariantLabel(video.latestVariant),
      ]
        .map(value => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(normalizedSearch);
    });
  }, [videoLibraryModeFilter, videoLibrarySearch, videoLibraryStatusFilter, videos]);
  const selectedLibraryVideo = useMemo(
    () => filteredVideos.find(video => video.id === selectedLibraryVideoId) || videos.find(video => video.id === selectedLibraryVideoId) || null,
    [filteredVideos, selectedLibraryVideoId, videos]
  );

  useEffect(() => {
    setVideoPlayerLoading(Boolean(videoPlayerOpen && selectedLibraryVideo));
  }, [selectedLibraryVideo?.id, videoPlayerOpen]);

  useEffect(() => {
    if (resolvedAllowedStudioViews.includes(initialStudioView)) {
      setStudioView(initialStudioView);
      return;
    }
    setStudioView(resolvedAllowedStudioViews[0]);
  }, [initialStudioView, allowedStudioViewsKey, resolvedAllowedStudioViews]);

  useEffect(() => {
    if (!initialGoalRequestId || appliedInitialGoalRequestRef.current === initialGoalRequestId) return;
    if (!initialSimpleGoalId || !simpleConfig?.goals?.some(goal => goal.id === initialSimpleGoalId)) return;
    const nextGoal = getGoalConfig(simpleConfig.goals, initialSimpleGoalId);
    if (!nextGoal) return;
    setUiMode('simple');
    setSimpleGoalId(nextGoal.id);
    setSimpleOrientationId(nextGoal.defaultOrientationId);
    setPromptDirty(false);
    setPrompt(
      getCurrentRecommendedPrompt({
        goal: nextGoal,
        modelConfig: selectedSimpleModelConfig,
        hasCharacterReferences: shouldUseCharacterReferenceImages,
        allowCharacterReferenceTemplates: true,
      })
    );
    appliedInitialGoalRequestRef.current = initialGoalRequestId;
  }, [
    getCurrentRecommendedPrompt,
    initialSimpleGoalId,
    initialGoalRequestId,
    selectedSimpleModelConfig,
    shouldUseCharacterReferenceImages,
    simpleConfig,
  ]);

  useEffect(() => {
    if (!initialGoalRequestId || appliedInitialSourceRequestRef.current === initialGoalRequestId) return;
    if (!initialSimpleGoalId || !inspirationItems.length) return;
    if (initialSimpleGoalId === 'animated_cover') {
      setSelectedInspirationIds([]);
      appliedInitialSourceRequestRef.current = initialGoalRequestId;
      return;
    }
    const approved = sortMarketingInspirationItems(
      inspirationItems.filter(item => String(item.status || '').toLowerCase() === 'approved')
    );
    if (!approved.length) return;
    const preferredType = initialSimpleGoalId === 'world_mood_teaser' ? 'scene' : 'vibe';
    const suggested = approved.find(item => item.type === preferredType) || approved[0];
    if (suggested) setSelectedInspirationIds([suggested.id]);
    appliedInitialSourceRequestRef.current = initialGoalRequestId;
  }, [initialGoalRequestId, initialSimpleGoalId, inspirationItems]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(VIDEO_STUDIO_UI_MODE_STORAGE_KEY, uiMode);
  }, [uiMode]);

  useEffect(() => {
    if (!hasAvailableCoverReference) {
      setUseCoverReference(false);
      return;
    }
    if (coverReferenceRequired) {
      setUseCoverReference(true);
    }
  }, [coverReferenceRequired, hasAvailableCoverReference]);

  useEffect(() => {
    if (uiMode === 'simple' && videoStyle === 'custom') {
      setVideoStyle(DEFAULT_VIDEO_STYLE);
    }
  }, [uiMode, videoStyle]);

  useEffect(() => {
    setSelectedCharacterReferenceIds(current =>
      current.filter(assetId => characterAssets.some(asset => asset.id === assetId))
    );
  }, [characterAssets]);

  useEffect(() => {
    const preferredOutput = getPreferredOutputOption(selectedModelConfig, selectedResolution);
    if (preferredOutput && preferredOutput !== selectedResolution) {
      setSelectedResolution(preferredOutput);
    }
    if (selectedModelConfig?.supportedDurations && !selectedModelConfig.supportedDurations.includes(durationSeconds)) {
      const fallbackDuration =
        selectedModelConfig.defaultDurationSeconds ||
        selectedModelConfig.supportedDurations[selectedModelConfig.supportedDurations.length - 1] ||
        8;
      setDurationSeconds(fallbackDuration);
    }
    const supportedAspectRatios = selectedModelConfig?.supportedAspectRatios || [];
    if (!usesSizeOptions && supportedAspectRatios.length > 0 && !supportedAspectRatios.includes(aspectRatio)) {
      setAspectRatio(supportedAspectRatios[0]);
    }
    if (selectedModelConfig?.supportsAudio && !selectedModelConfig.audioOptional && !generateAudio) {
      setGenerateAudio(true);
    }
  }, [aspectRatio, durationSeconds, generateAudio, selectedModelConfig, selectedResolution, usesSizeOptions]);

  useEffect(() => {
    if (!simpleConfig?.goals?.length) return;
    const nextGoal =
      simpleConfig.goals.find(goal => goal.id === simpleGoalId) ||
      simpleConfig.goals.find(goal => goal.id === simpleConfig.defaultGoalId) ||
      simpleConfig.goals[0];
    if (nextGoal.id !== simpleGoalId) {
      setSimpleGoalId(nextGoal.id);
      return;
    }
    const nextOrientation =
      nextGoal.orientations.find(orientation => orientation.id === simpleOrientationId) ||
      nextGoal.orientations.find(orientation => orientation.id === nextGoal.defaultOrientationId) ||
      nextGoal.orientations[0];
    if (nextOrientation.id !== simpleOrientationId) {
      setSimpleOrientationId(nextOrientation.id);
      return;
    }
    const nextLength =
      nextOrientation.lengths.find(option => option.seconds === simpleLengthSeconds) ||
      nextOrientation.lengths.find(option => option.seconds === nextOrientation.defaultLengthSeconds) ||
      nextOrientation.lengths[0];
    if (nextLength.seconds !== simpleLengthSeconds) {
      setSimpleLengthSeconds(nextLength.seconds);
    }
  }, [simpleConfig, simpleGoalId, simpleLengthSeconds, simpleOrientationId]);

  useEffect(() => {
    if (!visibleGoalOptions.length) return;
    if (visibleGoalOptions.some(goal => goal.id === simpleGoalId)) return;
    const nextGoal = visibleGoalOptions[0];
    setSimpleGoalId(nextGoal.id);
    setSimpleOrientationId(nextGoal.defaultOrientationId);
    if (uiMode === 'simple' && promptInitializedRef.current && !promptDirty) {
      setPrompt(
        getCurrentRecommendedPrompt({
          goal: nextGoal,
          modelConfig: selectedSimpleModelConfig,
          hasCharacterReferences: shouldUseCharacterReferenceImages,
          allowCharacterReferenceTemplates: true,
        })
      );
    }
  }, [
    getCurrentRecommendedPrompt,
    shouldUseCharacterReferenceImages,
    selectedSimpleModelConfig,
    simpleGoalId,
    promptDirty,
    uiMode,
    visibleGoalOptions,
  ]);

  useEffect(() => {
    if (!simpleConfig?.goals?.length) return;
    const nextAdvancedGoal =
      simpleConfig.goals.find(goal => goal.id === advancedGoalId) ||
      simpleConfig.goals.find(goal => goal.id === simpleConfig.defaultGoalId) ||
      simpleConfig.goals[0];
    if (nextAdvancedGoal.id !== advancedGoalId) {
      setAdvancedGoalId(nextAdvancedGoal.id);
      setMode(nextAdvancedGoal.mode);
      const targetAspectRatio = nextAdvancedGoal.defaultAspectRatio || '16:9';
      setAspectRatio(targetAspectRatio);
      const nextOutput = getLowestQualityOutputOption(selectedModelConfig, targetAspectRatio);
      if (nextOutput) {
        setSelectedResolution(nextOutput);
      }
    }
  }, [advancedGoalId, selectedModelConfig, simpleConfig]);

  useEffect(() => {
    if (!visibleGoalOptions.length) return;
    if (visibleGoalOptions.some(goal => goal.id === advancedGoalId)) return;
    const nextGoal = visibleGoalOptions[0];
    setAdvancedGoalId(nextGoal.id);
    setMode(nextGoal.mode);
    const targetAspectRatio = nextGoal.defaultAspectRatio || '16:9';
    setAspectRatio(targetAspectRatio);
    const nextOutput = getLowestQualityOutputOption(selectedModelConfig, targetAspectRatio);
    if (nextOutput) {
      setSelectedResolution(nextOutput);
    }
    if (uiMode === 'advanced' && promptInitializedRef.current && !promptDirty) {
      setPrompt(
        getCurrentRecommendedPrompt({
          goal: nextGoal,
          modelConfig: selectedModelConfig,
          hasCharacterReferences: false,
          allowCharacterReferenceTemplates: false,
        })
      );
    }
  }, [
    advancedGoalId,
    getCurrentRecommendedPrompt,
    selectedModelConfig,
    promptDirty,
    uiMode,
    visibleGoalOptions,
  ]);

  useEffect(() => {
    if (!selectedSimpleLengthOption) return;
    setSimpleGenerateAudio(selectedSimpleLengthOption.defaultGenerateAudio);
  }, [
    selectedSimpleLengthOption?.resolvedModelId,
    selectedSimpleLengthOption?.resolvedResolution,
    selectedSimpleLengthOption?.seconds,
  ]);

  useEffect(() => {
    if (uiMode !== 'simple' || !promptInitializedRef.current || !selectedSimpleGoal || promptDirty) return;
    setPrompt(
      getCurrentRecommendedPrompt({
        goal: selectedSimpleGoal,
        modelConfig: selectedSimpleModelConfig,
        hasCharacterReferences: shouldUseCharacterReferenceImages,
        allowCharacterReferenceTemplates: true,
      })
    );
  }, [
    currentSimpleGoalSupportsCharacterReferences,
    getCurrentRecommendedPrompt,
    shouldUseCharacterReferenceImages,
    selectedSimpleGoal,
    selectedSimpleModelConfig,
    promptDirty,
    uiMode,
  ]);

  useEffect(() => {
    if (uiMode !== 'advanced' || !promptInitializedRef.current || !selectedAdvancedGoal || promptDirty) return;
    setPrompt(
      getCurrentRecommendedPrompt({
        goal: selectedAdvancedGoal,
        modelConfig: selectedModelConfig,
        hasCharacterReferences: false,
        allowCharacterReferenceTemplates: false,
      })
    );
  }, [getCurrentRecommendedPrompt, promptDirty, selectedAdvancedGoal, selectedModelConfig, uiMode]);

  useEffect(() => {
    if (promptInitializedRef.current) return;
    if (!simpleConfig?.goals?.length || !selectedSimpleGoal) return;
    if (modelCatalog.length > 0 && !selectedSimpleModelConfig) return;
    setPrompt(
      getCurrentRecommendedPrompt({
        goal: selectedSimpleGoal,
        modelConfig: selectedSimpleModelConfig,
        hasCharacterReferences: shouldUseCharacterReferenceImages,
        allowCharacterReferenceTemplates: true,
      })
    );
    promptInitializedRef.current = true;
  }, [getCurrentRecommendedPrompt, modelCatalog.length, selectedSimpleGoal, selectedSimpleModelConfig, shouldUseCharacterReferenceImages, simpleConfig]);

  const refreshModelCatalog = useCallback(async () => {
    if (!bookId || !userId) return;
    setIsLoadingModels(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/models?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setModelCatalog(items);
      const defaultModel = String(data?.defaultModel || items[0]?.id || '');
      setModel(currentModel => {
        if (items.some((item: MarketingVideoModelCatalogItem) => item.id === currentModel)) {
          return currentModel;
        }
        return defaultModel;
      });
    } catch (error) {
      console.error('Failed to load marketing video model catalog', error);
      message.error('Failed to load marketing video models');
      setModelCatalog([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, [apiBase, bookId, userId]);

  const refreshSimpleConfig = useCallback(async () => {
    if (!bookId || !userId) return;
    setIsLoadingSimpleConfig(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/simple-config?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      setSimpleConfig(data);
    } catch (error) {
      console.error('Failed to load marketing video simple config', error);
      message.error('Failed to load guided video options');
      setSimpleConfig(null);
    } finally {
      setIsLoadingSimpleConfig(false);
    }
  }, [apiBase, bookId, userId]);

  const refreshCharacterAssets = useCallback(async () => {
    if (!bookId || !userId) return;
    setIsLoadingCharacterAssets(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      setCharacterAssets(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to load marketing character assets', error);
      message.error('Failed to load character portrait references');
      setCharacterAssets([]);
    } finally {
      setIsLoadingCharacterAssets(false);
    }
  }, [apiBase, bookId, userId]);

  const refreshInspiration = useCallback(async () => {
    if (!bookId || !userId) return;
    setInspirationLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      if (sourceLanguage) params.set('sourceLanguage', sourceLanguage);
      params.set('editionLanguage', resolvedLanguage);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      const nextItems = sortMarketingInspirationItems(Array.isArray(data?.items) ? data.items : []);
      setInspirationItems(nextItems);
      setSelectedInspirationIds(prev => prev.filter(itemId => nextItems.some(item => item.id === itemId)));
    } catch (error) {
      console.error('Failed to load marketing inspiration items', error);
      message.error('Failed to load inspiration items');
      setInspirationItems([]);
    } finally {
      setInspirationLoading(false);
    }
  }, [apiBase, bookId, resolvedLanguage, sourceLanguage, userId]);

  const refreshVideos = useCallback(async () => {
    if (!bookId || !userId) return;
    setIsLoadingVideos(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      params.set('page', '1');
      params.set('pageSize', '30');
      if (videoLibraryEditionScope === 'current') params.set('language', resolvedLanguage);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      setVideos(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to load marketing videos', error);
      message.error('Failed to load marketing videos');
    } finally {
      setIsLoadingVideos(false);
    }
  }, [apiBase, bookId, resolvedLanguage, userId, videoLibraryEditionScope]);

  const toggleCharacterReferenceAsset = useCallback((asset: MarketingCharacterAsset) => {
    setSelectedCharacterReferenceIds(current => {
      if (current.includes(asset.id)) {
        return current.filter(id => id !== asset.id);
      }
      if (current.length >= MAX_CHARACTER_REFERENCE_ASSETS) {
        message.info(`Select up to ${MAX_CHARACTER_REFERENCE_ASSETS} character portraits per video.`);
        return current;
      }
      return [...current, asset.id];
    });
  }, []);

  const applyAdvancedModelSelection = useCallback(
    (value: string, nextGoal?: MarketingVideoSimpleGoalConfig | null) => {
      setModel(value);
      const nextConfig = modelCatalog.find(option => option.id === value) || null;
      const nextResolution = getPreferredOutputOption(nextConfig, selectedResolution);
      if (nextResolution) {
        setSelectedResolution(nextResolution);
      }
      if (nextConfig && !nextConfig.supportedDurations.includes(durationSeconds)) {
        setDurationSeconds(nextConfig.defaultDurationSeconds || nextConfig.supportedDurations[0] || 8);
      }
      if (
        nextConfig &&
        !String(nextConfig.defaultResolution || '').includes('x') &&
        nextConfig.supportedAspectRatios?.length &&
        !nextConfig.supportedAspectRatios.includes(aspectRatio)
      ) {
        setAspectRatio(nextConfig.supportedAspectRatios[0]);
      }
      const goalForPrompt = nextGoal || selectedAdvancedGoal;
      if (goalForPrompt && !promptDirty) {
        setPrompt(
          getCurrentRecommendedPrompt({
            goal: goalForPrompt,
            modelConfig: nextConfig,
            hasCharacterReferences: false,
            allowCharacterReferenceTemplates: false,
          })
        );
      }
      return nextConfig;
    },
    [
      aspectRatio,
      durationSeconds,
      getCurrentRecommendedPrompt,
      modelCatalog,
      promptDirty,
      selectedAdvancedGoal,
      selectedResolution,
    ]
  );

  useEffect(() => {
    if (uiMode !== 'advanced' || mode !== 'cover_animate' || selectedModelConfig?.supportsLastFrameImage) return;
    const compatibleModel = modelCatalog.find(option => option.supportsLastFrameImage);
    if (compatibleModel) {
      applyAdvancedModelSelection(compatibleModel.id);
    }
  }, [applyAdvancedModelSelection, mode, modelCatalog, selectedModelConfig, uiMode]);

  useEffect(() => {
    if (!createViewAllowed) return;
    void refreshModelCatalog();
    void refreshSimpleConfig();
    void refreshCharacterAssets();
    void refreshInspiration();
  }, [createViewAllowed, refreshCharacterAssets, refreshInspiration, refreshModelCatalog, refreshSimpleConfig]);

  useEffect(() => {
    if (inspirationRefreshSignal === undefined) return;
    if (lastInspirationRefreshSignalRef.current === inspirationRefreshSignal) return;
    lastInspirationRefreshSignalRef.current = inspirationRefreshSignal;
    if (createViewAllowed) void refreshInspiration();
  }, [createViewAllowed, inspirationRefreshSignal, refreshInspiration]);

  useEffect(() => {
    void refreshVideos();
  }, [refreshVideos]);

  useEffect(() => {
    const nextStatuses = new Map(videos.map(video => [video.id, String(video.status || '').toLowerCase()]));
    const previousStatuses = seenVideoStatusesRef.current;
    if (previousStatuses) {
      videos.forEach(video => {
        const nextStatus = nextStatuses.get(video.id);
        const previousStatus = previousStatuses.get(video.id);
        if (previousStatus && previousStatus !== 'completed' && nextStatus === 'completed') {
          message.success('Your marketing video is ready in Created Videos.');
        }
        if (previousStatus && previousStatus !== 'failed' && nextStatus === 'failed') {
          message.error('A marketing video could not be completed. Open Created Videos for details.');
        }
      });
    }
    seenVideoStatusesRef.current = nextStatuses;
  }, [videos]);

  useEffect(() => {
    const hasPending = videos.some(video => {
      const status = (video.status || '').toLowerCase();
      return status === 'queued' || status === 'pending' || status === 'running' || status === 'retrying';
    });
    if (!hasPending) return;
    const timer = window.setInterval(() => {
      void refreshVideos();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [refreshVideos, videos]);

  useEffect(() => {
    if (!videos.length) {
      setSelectedLibraryVideoId('');
      return;
    }
    if (!videos.some(video => video.id === selectedLibraryVideoId)) {
      setSelectedLibraryVideoId(videos[0]?.id || '');
    }
  }, [selectedLibraryVideoId, videos]);

  const applyGoalTemplate = useCallback(
    (goalId: MarketingVideoSimpleGoal, force = false) => {
      const nextGoal = getGoalConfig(simpleConfig?.goals, goalId);
      if (nextGoal?.defaultOrientationId) {
        setSimpleOrientationId(nextGoal.defaultOrientationId);
      }
      if (!promptDirty || force) {
        setPrompt(
          getCurrentRecommendedPrompt({
            goal: nextGoal,
            modelConfig: selectedSimpleModelConfig,
            hasCharacterReferences: shouldUseCharacterReferenceImages,
            allowCharacterReferenceTemplates: true,
          })
        );
        setPromptDirty(false);
      }
    },
    [getCurrentRecommendedPrompt, promptDirty, selectedSimpleModelConfig, shouldUseCharacterReferenceImages, simpleConfig]
  );

  const applyAdvancedPreset = useCallback(
    (goalId: MarketingVideoSimpleGoal, nextModelConfig?: MarketingVideoModelCatalogItem | null) => {
      const nextGoal = getGoalConfig(simpleConfig?.goals, goalId);
      if (!nextGoal) return;
      const targetAspectRatio = nextGoal.defaultAspectRatio || '16:9';
      const targetModelConfig = nextModelConfig ?? selectedModelConfig;
      setAdvancedGoalId(nextGoal.id);
      setMode(nextGoal.mode);
      setAspectRatio(targetAspectRatio);
      const nextOutput = getLowestQualityOutputOption(targetModelConfig, targetAspectRatio);
      if (nextOutput) {
        setSelectedResolution(nextOutput);
      }
      if (!promptDirty) {
        setPrompt(
          getCurrentRecommendedPrompt({
            goal: nextGoal,
            modelConfig: targetModelConfig,
            hasCharacterReferences: false,
            allowCharacterReferenceTemplates: false,
          })
        );
      }
    },
    [getCurrentRecommendedPrompt, promptDirty, selectedModelConfig, simpleConfig]
  );

  const handleUiModeChange = useCallback(
    (value: string | number) => {
      const nextMode = String(value) as MarketingVideoUiMode;
      if (nextMode === 'advanced' && selectedSimpleLengthOption) {
        const nextRequest = selectedSimpleLengthOption.resolvedRequest;
        setAdvancedGoalId(selectedSimpleGoal?.id || advancedGoalId);
        setMode(nextRequest.mode);
        setModel(nextRequest.model);
        setAspectRatio(nextRequest.aspectRatio);
        setSelectedResolution(nextRequest.resolution);
        setDurationSeconds(nextRequest.durationSeconds);
        setGenerateAudio(effectiveSimpleGenerateAudio);
        if (!promptDirty) {
          setPrompt(
            getCurrentRecommendedPrompt({
              goal: selectedSimpleGoal,
              modelConfig: selectedSimpleModelConfig,
              hasCharacterReferences: false,
              allowCharacterReferenceTemplates: false,
            })
          );
        }
      }
      if (nextMode === 'simple') {
        setSimpleGoalId(selectedAdvancedGoal?.id || simpleGoalId);
        if (selectedAdvancedGoal?.defaultOrientationId) {
          setSimpleOrientationId(selectedAdvancedGoal.defaultOrientationId);
        }
      }
      setUiMode(nextMode);
    },
    [
      advancedGoalId,
      effectiveSimpleGenerateAudio,
      getCurrentRecommendedPrompt,
      promptDirty,
      selectedAdvancedGoal?.id,
      selectedSimpleGoal,
      selectedSimpleLengthOption,
      selectedSimpleModelConfig,
      simpleGoalId,
    ]
  );

  const handleResetPrompts = useCallback(() => {
    if (uiMode === 'simple') {
      applyGoalTemplate(simpleGoalId, true);
      message.success('Reapplied the guided prompt starting point.');
      return;
    }
    setPrompt(
      getCurrentRecommendedPrompt({
        goal: selectedAdvancedGoal,
        modelConfig: selectedModelConfig,
        hasCharacterReferences: false,
        allowCharacterReferenceTemplates: false,
      })
    );
    setPromptDirty(false);
    message.success('Reapplied recommended prompts for this preset.');
  }, [applyGoalTemplate, getCurrentRecommendedPrompt, selectedAdvancedGoal, selectedModelConfig, simpleGoalId, uiMode]);

  const handleGenerate = useCallback(async () => {
    const promptValue = prompt.trim();
    const customStyleValue = customVideoStyle.trim();
    if (!promptValue) {
      message.error('Enter a prompt before generating.');
      return;
    }
    if (uiMode === 'advanced' && videoStyle === 'custom' && !customStyleValue) {
      message.error('Enter a custom style description before generating.');
      return;
    }
    if (coverReferenceRequirementMessage) {
      message.error(coverReferenceRequirementMessage);
      return;
    }
    if (coverEndFrameSupportMessage) {
      message.error(coverEndFrameSupportMessage);
      return;
    }
    const activeCharacterReferenceIds = shouldUseCharacterReferenceImages
      ? activeCharacterReferenceAssets.map(asset => asset.id)
      : [];
    const finalPromptValue = appendCharacterDescriptionFallbackToPrompt(promptValue, characterDescriptionFallbackLines);

    const requestBody =
      uiMode === 'simple'
        ? selectedSimpleLengthOption
          ? {
              userId,
              ...selectedSimpleLengthOption.resolvedRequest,
              language: resolvedLanguage,
              generateAudio: effectiveSimpleGenerateAudio,
              prompt: finalPromptValue,
              visualStyle: videoStyle,
              customVisualStyle: videoStyle === 'custom' ? customStyleValue : undefined,
              useCoverReference: effectiveUseCoverReference,
              characterReferenceAssetIds: activeCharacterReferenceIds,
              inspirationItemIds: selectedInspirationIds,
              uiMode: 'simple' as const,
            }
          : null
        : selectedModelConfig
          ? {
              userId,
              language: resolvedLanguage,
              mode,
              model,
              prompt: finalPromptValue,
              durationSeconds,
              aspectRatio: effectiveAspectRatio,
              sampleCount,
              generateAudio: effectiveGenerateAudio,
              negativePrompt: negativePrompt.trim() || undefined,
              seed: seed ?? undefined,
              resolution,
              visualStyle: videoStyle,
              customVisualStyle: videoStyle === 'custom' ? customStyleValue : undefined,
              useCoverReference: effectiveUseCoverReference,
              characterReferenceAssetIds: activeCharacterReferenceIds,
              inspirationItemIds: selectedInspirationIds,
              uiMode: 'advanced' as const,
            }
          : null;

    if (!requestBody) {
      message.error('Video generation options are still loading.');
      return;
    }
    if (!hasEnoughCredits) {
      message.error(`Insufficient credits. You need ${activePricingEstimate.estimatedCredits.toLocaleString()} credits.`);
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to queue marketing video generation.'));
      }
      message.success('Marketing video generation queued.');
      await refreshVideos();
      if (fetchCreditBalance) {
        await fetchCreditBalance(userId);
      }
    } catch (error: any) {
      console.error('Failed to generate marketing video', error);
      message.error(error?.message || 'Failed to queue marketing video generation');
    } finally {
      setIsGenerating(false);
    }
  }, [
    activePricingEstimate.estimatedCredits,
    activeCharacterReferenceAssets,
    apiBase,
    bookId,
    characterDescriptionFallbackLines,
    coverReferenceRequirementMessage,
    coverEndFrameSupportMessage,
    customVideoStyle,
    durationSeconds,
    effectiveAspectRatio,
    effectiveGenerateAudio,
    effectiveUseCoverReference,
    effectiveSimpleGenerateAudio,
    fetchCreditBalance,
    hasEnoughCredits,
    mode,
    model,
    negativePrompt,
    prompt,
    refreshVideos,
    resolution,
    resolvedLanguage,
    sampleCount,
    seed,
    selectedModelConfig,
    selectedInspirationIds,
    selectedSimpleLengthOption,
    shouldUseCharacterReferenceImages,
    videoStyle,
    uiMode,
    userId,
  ]);

  const handleDeleteVideo = useCallback(
    async (video: MarketingVideoRecord) => {
      try {
        const response = await fetch(
          `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/${encodeURIComponent(video.id)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
          }
        );
        if (!response.ok) {
          throw new Error(await response.text());
        }
        message.success('Video deleted');
        setVideoPlayerOpen(false);
        setSelectedLibraryVideoId('');
        await refreshVideos();
      } catch (error) {
        console.error('Failed to delete marketing video', error);
        message.error('Failed to delete video');
      }
    },
    [apiBase, bookId, refreshVideos, userId]
  );

  const handleDownload = useCallback(
    async (video: MarketingVideoRecord) => {
      try {
        const fallbackHistoryId = video.latestHistoryId || null;
        const downloadUrl =
          video.signedUrl ||
          (fallbackHistoryId
            ? buildHistoryStreamUrl({
                apiBase,
                bookId,
                historyId: fallbackHistoryId,
                userId,
                download: true,
                filename: `marketing_video_${video.id}.mp4`,
              })
            : null);
        if (!downloadUrl) {
          message.error('No downloadable file found for this video yet.');
          return;
        }
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = `marketing_video_${video.id}.mp4`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } catch (error) {
        console.error('Failed to download marketing video', error);
        message.error('Failed to download video');
      }
    },
    [apiBase, bookId, userId]
  );

  useEffect(() => {
    if (!actionSignal?.ts) return;
    if (lastProcessedActionTsRef.current === actionSignal.ts) return;
    lastProcessedActionTsRef.current = actionSignal.ts;
    if (actionSignal.action === 'generate') {
      void handleGenerate();
      return;
    }
    if (actionSignal.action === 'refresh') {
      void refreshVideos();
      if (createViewAllowed) {
        void refreshCharacterAssets();
        void refreshInspiration();
      }
      return;
    }
    if (actionSignal.action === 'copy') {
      if (!prompt.trim()) {
        message.info('Write a prompt first.');
        return;
      }
      void copyText(prompt, 'Prompt copied to clipboard.');
    }
  }, [actionSignal, createViewAllowed, handleGenerate, prompt, refreshCharacterAssets, refreshInspiration, refreshVideos]);

  const advancedPresetValue = selectedAdvancedGoal?.id || advancedGoalId;
  const outputLabel = usesSizeOptions ? 'Output Size' : 'Resolution';
  const showStudioViewToggle = resolvedAllowedStudioViews.length > 1;
  const createSectionStyle: React.CSSProperties = {
    background: 'var(--surface-muted)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 10,
    padding: 10,
  };
  const compactInfoStyle: React.CSSProperties = {
    background: 'rgba(24, 144, 255, 0.08)',
    border: '1px solid rgba(24, 144, 255, 0.22)',
    borderRadius: 10,
    padding: '10px 12px',
  };
  const compactWarningStyle: React.CSSProperties = {
    background: 'rgba(250, 173, 20, 0.10)',
    border: '1px solid rgba(250, 173, 20, 0.22)',
    borderRadius: 10,
    padding: '10px 12px',
  };
  const overlayCardStyle: React.CSSProperties = {
    background: 'rgba(15, 23, 42, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    color: '#fff',
  };
  const disabledCharacterReferenceReason =
    'This model can use text prompts and, in some cases, cover or frame conditioning, but it does not support extra image-reference inputs such as character portraits.';
  const advancedModelOptions = modelCatalog.map(option => {
    const showFallbackTooltip = selectedCharacterReferenceAssets.length > 0 && !option.supportsReferenceImages;
    const cannotTargetCoverEnding = mode === 'cover_animate' && !option.supportsLastFrameImage;
    const modelTooltip = cannotTargetCoverEnding
      ? 'Cover Animate requires a model that supports an exact ending frame.'
      : showFallbackTooltip
        ? disabledCharacterReferenceReason
        : null;
    return {
      label: modelTooltip ? (
        <Tooltip title={modelTooltip}>
          <span>{option.displayName}</span>
        </Tooltip>
      ) : (
        option.displayName
      ),
      value: option.id,
      disabled: cannotTargetCoverEnding,
    };
  });
  const simpleLengthOptions = (selectedSimpleOrientation?.lengths || []).map(option => {
    const optionModel = modelCatalog.find(modelItem => modelItem.id === option.resolvedModelId);
    const showFallbackTooltip =
      selectedCharacterReferenceAssets.length > 0 &&
      currentSimpleGoalSupportsCharacterReferences &&
      !optionModel?.supportsReferenceImages;
    return {
      label: showFallbackTooltip ? (
        <Tooltip title={disabledCharacterReferenceReason}>
          <span>{`${option.seconds}s`}</span>
        </Tooltip>
      ) : (
        `${option.seconds}s`
      ),
      value: option.seconds,
      disabled: false,
    };
  });

  return (
    <>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {showStudioViewToggle ? (
          <Segmented
            block
            value={studioView}
            onChange={value => setStudioView(String(value) as 'create' | 'library')}
            options={resolvedAllowedStudioViews.map(view => ({
              label: view === 'create' ? 'Create' : 'Created Videos',
              value: view,
            }))}
          />
        ) : null}

        {studioView === 'create' ? (
          <Card
            size="small"
            title="Generate Marketing Video"
          >
            <Segmented
              block
              value={uiMode}
              onChange={handleUiModeChange}
              options={[
                { label: 'Simple', value: 'simple' },
                { label: 'Advanced', value: 'advanced' },
              ]}
              style={{ marginBottom: 12 }}
            />

            <Row gutter={[14, 12]} align="top">
              <Col xs={24} lg={13}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <div style={createSectionStyle}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Text strong>{uiMode === 'simple' ? 'Quick Setup' : 'Creative Setup'}</Text>
                      <Form layout="vertical" style={{ marginBottom: -12 }}>
                        <Form.Item label="Preset Collection">
                          <Select
                            value={presetCollectionFilter}
                            onChange={(value: PresetCollectionFilter) => setPresetCollectionFilter(value)}
                            loading={isLoadingSimpleConfig}
                            options={presetCollectionOptions}
                          />
                        </Form.Item>
                        {uiMode === 'simple' ? (
                          <>
                            <Row gutter={12}>
                              <Col xs={24} md={8}>
                                <Form.Item label="Goal">
                                  <Select
                                    value={simpleGoalId}
                                    onChange={(value: MarketingVideoSimpleGoal) => {
                                      setSimpleGoalId(value);
                                      applyGoalTemplate(value);
                                    }}
                                    loading={isLoadingSimpleConfig}
                                    showSearch
                                    optionFilterProp="searchText"
                                    optionRender={option => buildPresetOptionLabel(option.data as MarketingVideoSimpleGoalConfig)}
                                    options={visibleGoalOptions.map(goal => ({
                                      ...goal,
                                      value: goal.id,
                                      searchText: buildPresetSearchText(goal),
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8}>
                                <Form.Item label="Orientation">
                                  <Select
                                    value={simpleOrientationId}
                                    onChange={value => setSimpleOrientationId(value)}
                                    loading={isLoadingSimpleConfig}
                                    options={(selectedSimpleGoal?.orientations || []).map(orientation => ({
                                      label:
                                        orientation.id === selectedSimpleGoal?.defaultOrientationId
                                          ? `${orientation.label} (Suggested)`
                                          : orientation.label,
                                      value: orientation.id,
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8}>
                                <Form.Item label="Length">
                                  <Select
                                    value={simpleLengthSeconds}
                                    onChange={value => setSimpleLengthSeconds(value)}
                                    loading={isLoadingSimpleConfig}
                                    options={simpleLengthOptions}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            {selectedSimpleLengthOption ? (
                              <Text type="secondary">
                                Recommended route: {selectedSimpleLengthOption.resolvedModelDisplayName} ·{' '}
                                {selectedSimpleLengthOption.resolvedResolution} ·{' '}
                                {selectedSimpleLengthOption.estimatedCredits.toLocaleString()} estimated credits.
                                Choose Customize to select the model and output quality yourself.
                              </Text>
                            ) : null}
                            {selectedSimpleLengthOption?.audioSupported && selectedSimpleLengthOption.audioOptional ? (
                              <Form.Item
                                label="Generate Audio"
                                valuePropName="checked"
                                extra="Adds music and ambience only; speech, dialogue, lyrics, and voiceover are not included."
                              >
                                <Switch
                                  checked={effectiveSimpleGenerateAudio}
                                  onChange={value => setSimpleGenerateAudio(value)}
                                />
                              </Form.Item>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <Row gutter={12}>
                              <Col xs={24} md={12}>
                                <Form.Item label="Video Type">
                                  <Select
                                    value={advancedPresetValue}
                                    onChange={(value: MarketingVideoSimpleGoal) => applyAdvancedPreset(value)}
                                    showSearch
                                    optionFilterProp="searchText"
                                    optionRender={option => buildPresetOptionLabel(option.data as MarketingVideoSimpleGoalConfig)}
                                    options={visibleGoalOptions.map(goal => ({
                                      ...goal,
                                      value: goal.id,
                                      searchText: buildPresetSearchText(goal),
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item label="Model">
                                  <Select
                                    value={model}
                                    onChange={value => applyAdvancedModelSelection(value)}
                                    loading={isLoadingModels}
                                    options={advancedModelOptions}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={12}>
                              <Col xs={24} md={12}>
                                <Form.Item label={outputLabel}>
                                  <Select
                                    value={resolution}
                                    onChange={value => setSelectedResolution(value)}
                                    options={selectableOutputOptions.map(option => ({
                                      label: usesSizeOptions ? formatOutputSizeLabel(option) : option,
                                      value: option,
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item label="Final Video Length (seconds)">
                                  <Select
                                    value={durationSeconds}
                                    onChange={value => setDurationSeconds(value)}
                                    options={durationOptions.map(duration => ({
                                      label: `${duration}s`,
                                      value: duration,
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            {!usesSizeOptions ? (
                              <Form.Item label="Aspect Ratio">
                                <Select
                                  value={aspectRatio}
                                  onChange={value => setAspectRatio(value)}
                                  options={(selectedModelConfig?.supportedAspectRatios || []).map(value => ({
                                    label:
                                      value === selectedAdvancedGoal?.defaultAspectRatio
                                        ? `${formatAspectRatioLabel(value)} (Suggested)`
                                        : formatAspectRatioLabel(value),
                                    value,
                                  }))}
                                />
                              </Form.Item>
                            ) : null}
                          </>
                        )}
                      </Form>
                      <Form layout="vertical" style={{ marginBottom: -12 }}>
                        <Row gutter={12}>
                          <Col xs={24} md={12}>
                            <Form.Item label="Style">
                              <Select
                                value={videoStyle}
                                onChange={value => setVideoStyle(value)}
                                options={VIDEO_STYLE_OPTIONS.filter(option => uiMode === 'advanced' || option.value !== 'custom').map(option => ({
                                  value: option.value,
                                  label: option.label,
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Send Cover Reference"
                              valuePropName="checked"
                              extra={
                                coverReferenceRequired
                                  ? 'Required as the exact ending frame. It is not used as the opening frame.'
                                  : hasAvailableCoverReference
                                    ? 'Use the book cover as a visual anchor for style and motif continuity.'
                                    : 'No cover is available for this book yet.'
                              }
                            >
                              <Switch
                                checked={effectiveUseCoverReference}
                                disabled={!hasAvailableCoverReference || coverReferenceRequired}
                                onChange={value => setUseCoverReference(value)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                      {uiMode === 'advanced' && videoStyle === 'custom' ? (
                        <Input
                          value={customVideoStyle}
                          onChange={event => setCustomVideoStyle(event.target.value)}
                          placeholder="Describe the exact visual style, for example: stop-motion miniature fantasy with hand-painted textures and shallow depth of field"
                        />
                      ) : null}
                      <Text type="secondary">
                        {getVideoStyleInstruction(videoStyle, customVideoStyle)}
                      </Text>
                      {coverReferenceRequirementMessage ? (
                        <Alert type="warning" showIcon message={coverReferenceRequirementMessage} />
                      ) : null}
                      {coverEndFrameSupportMessage ? (
                        <Alert type="warning" showIcon message={coverEndFrameSupportMessage} />
                      ) : null}
                    </Space>
                  </div>

                  {!isLoadingSimpleConfig && !selectedSimpleLengthOption && uiMode === 'simple' ? (
                    <Alert
                      type="error"
                      showIcon
                      message="No guided video options are currently available."
                    />
                  ) : null}
                </Space>
              </Col>

              <Col xs={24} lg={11}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <div style={createSectionStyle}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>Prompt</Text>
                        <Text type="secondary">
                          Style: {getVideoStyleLabel(videoStyle, customVideoStyle)}
                          {effectiveUseCoverReference
                            ? coverReferenceRequired
                              ? ' | Cover set as ending frame'
                              : ' | Cover reference enabled'
                            : ' | Cover reference disabled'}
                        </Text>
                        {activeCharacterReferenceNames.length ? (
                          <Text type="secondary">
                            Character continuity anchors: {activeCharacterReferenceNames.join(', ')}
                          </Text>
                        ) : null}
                      </Space>
                      <TextArea
                        rows={isDesktopCreateLayout ? 6 : 5}
                        value={prompt}
                        onChange={event => {
                          setPrompt(event.target.value);
                          setPromptDirty(true);
                        }}
                        placeholder={
                          uiMode === 'simple'
                            ? 'Describe the feeling, imagery, and selling point you want in the clip.'
                            : 'Prompt for the video generation request'
                        }
                      />
                      {promptDirty ? (
                        <Alert
                          type="info"
                          showIcon
                          message="Your prompt edits are locked"
                          description="Changing presets, sources, styles, or models will not replace this prompt unless you reapply the recommended starting point."
                          action={<Button size="small" onClick={handleResetPrompts}>Reapply</Button>}
                        />
                      ) : null}
                    </Space>
                  </div>

                  <MarketingInspirationPicker
                    items={inspirationItems}
                    loading={inspirationLoading}
                    selectedIds={selectedInspirationIds}
                    onChange={setSelectedInspirationIds}
                    helperText="Select approved scenes, quotes, and vibes to enrich the promo prompt while keeping the current style and reference-image flow intact."
                  />
                  {!inspirationLoading && approvedInspirationCount === 0 && selectedSimpleGoal?.id !== 'animated_cover' ? (
                    <Alert
                      type="warning"
                      showIcon
                      message="No approved story sources yet"
                      description="Prepare the Book Kit to give this video reliable scenes, quotes, moods, and spoiler context. Your current draft will stay here."
                      action={onOpenSourceKit ? <Button size="small" onClick={onOpenSourceKit}>Prepare sources</Button> : undefined}
                    />
                  ) : null}

                  {(uiMode === 'advanced' || currentSimpleGoalSupportsCharacterReferences) ? (
                    <div style={createSectionStyle}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text strong>Character Portrait References</Text>
                          <Space size={8}>
                            <Button size="small" onClick={() => void refreshCharacterAssets()} loading={isLoadingCharacterAssets}>
                              Refresh Portraits
                            </Button>
                            <Button size="small" type="primary" onClick={() => setCharacterReferenceModalOpen(true)}>
                              Choose Portraits
                            </Button>
                          </Space>
                        </Space>
                        <Text type="secondary">
                          Reuse saved Character Studio portraits as identity anchors for the video. Up to {MAX_CHARACTER_REFERENCE_ASSETS} images.
                        </Text>
                        {activeImageReferenceSupportMessage ? (
                          <Alert type="info" showIcon message={activeImageReferenceSupportMessage} />
                        ) : null}
                        {activeCharacterReferenceNames.length ? (
                          <Text type="secondary">
                            Referencing: {activeCharacterReferenceNames.join(', ')}
                          </Text>
                        ) : null}
                        {!currentSimpleGoalSupportsCharacterReferences && uiMode === 'simple' ? (
                          <Alert
                            type="info"
                            showIcon
                            message="This simple preset does not use character portrait references."
                            description="Switch to Character Mood Clip, Romance Tension Teaser, Dark Romance Desire Teaser, Urban Fantasy Power Teaser, or use Advanced mode."
                          />
                        ) : null}
                        {selectedCharacterReferenceAssets.length ? (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text type="secondary">
                              {selectedCharacterReferenceAssets.length} portrait{selectedCharacterReferenceAssets.length === 1 ? '' : 's'} selected
                            </Text>
                            <Space wrap size={8}>
                              {selectedCharacterReferenceAssets.map(asset => (
                                <div
                                  key={`selected-reference-${asset.id}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    border: '1px solid var(--border-subtle)',
                                    background: 'var(--surface-raised)',
                                    borderRadius: 10,
                                    padding: '6px 8px',
                                  }}
                                >
                                  <img
                                    src={resolveCharacterAssetPreviewSrc(asset)}
                                    alt={asset.characterName || 'Character reference'}
                                    style={{
                                      width: 36,
                                      height: 36,
                                      objectFit: 'cover',
                                      borderRadius: 6,
                                      background: 'rgba(255,255,255,0.04)',
                                    }}
                                  />
                                  <Space direction="vertical" size={0}>
                                    <Text strong style={{ lineHeight: 1.2 }}>
                                      {asset.characterName || 'Character'}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
                                      {asset.stylePreset || 'Portrait'}
                                    </Text>
                                  </Space>
                                  <Button size="small" onClick={() => toggleCharacterReferenceAsset(asset)}>
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </Space>
                          </Space>
                        ) : null}
                        {!isLoadingCharacterAssets && !groupedCharacterAssets.length ? (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Generate character portraits first to use them as reference images."
                          />
                        ) : null}
                      </Space>
                    </div>
                  ) : null}

                  {uiMode === 'advanced' ? (
                    <div style={createSectionStyle}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text strong>Fine-Tune Settings</Text>
                          <Button type="link" onClick={() => setAdvancedDetailsOpen(open => !open)} style={{ paddingInline: 0 }}>
                            {advancedDetailsOpen ? 'Hide extra controls' : 'Show extra controls'}
                          </Button>
                        </Space>
                        {advancedDetailsOpen ? (
                          <Form layout="vertical" style={{ marginBottom: -12 }}>
                            <Row gutter={12}>
                              <Col xs={24} md={12}>
                                <Form.Item label="Samples">
                                  <InputNumber
                                    min={1}
                                    max={4}
                                    value={sampleCount}
                                    onChange={value => setSampleCount(Number(value || 1))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label={
                                    selectedModelConfig?.supportsAudio && !selectedModelConfig.audioOptional ? 'Audio' : 'Generate Audio'
                                  }
                                  valuePropName="checked"
                                  extra={
                                    selectedModelConfig?.supportsAudio && !selectedModelConfig.audioOptional
                                      ? 'This model includes music and ambience by default; speech, dialogue, lyrics, and voiceover are not included.'
                                      : 'Adds music and ambience only; speech, dialogue, lyrics, and voiceover are not included.'
                                  }
                                >
                                  <Switch
                                    checked={effectiveGenerateAudio}
                                    disabled={!selectedModelConfig?.supportsAudio || !selectedModelConfig?.audioOptional}
                                    onChange={value => setGenerateAudio(value)}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Form.Item label="Negative Prompt (optional)">
                              <TextArea
                                rows={2}
                                value={negativePrompt}
                                onChange={event => setNegativePrompt(event.target.value)}
                                placeholder="Short noun phrases for elements to avoid, like extra limbs, blurry faces, text overlays."
                              />
                            </Form.Item>
                            <Form.Item label="Seed (optional)">
                              <InputNumber
                                min={1}
                                max={2147483647}
                                value={seed}
                                onChange={value => setSeed(value == null ? null : Number(value))}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Form>
                        ) : (
                          <Text type="secondary">
                            Samples, audio override, negative prompt, and seed are hidden until you need them.
                          </Text>
                        )}
                      </Space>
                    </div>
                  ) : null}
                </Space>
              </Col>
            </Row>

            <div style={{ ...createSectionStyle, marginTop: 10 }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <CostSummary
                  creditBalance={creditBalance ? availableCredits : null}
                  baseCost={activePricingEstimate.estimatedCredits}
                  tooltipText="Video Studio credit cost is estimated from the selected model's pricing profile, billed duration, output size, audio setting, and sample count."
                  headerActionNode={
                    <Button size="small" onClick={() => setVideoCostBreakdownOpen(true)}>
                      View details
                    </Button>
                  }
                  containerStyle={{ marginBottom: 0 }}
                />
                {coverReferenceRequirementMessage ? (
                  <Alert type="warning" showIcon message={coverReferenceRequirementMessage} />
                ) : null}
                {!hasEnoughCredits && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Not enough credits for this request."
                    description={
                      <Button type="link" style={{ padding: 0 }} onClick={() => navigate('/account/billing')}>
                        Add credits
                      </Button>
                    }
                  />
                )}
                <Row gutter={[12, 12]}>
                  <Col xs={24}>
                    <Segmented
                      value={videoLibraryEditionScope}
                      onChange={value => setVideoLibraryEditionScope(value as 'current' | 'all')}
                      options={[
                        { label: `${resolvedLanguage} edition`, value: 'current' },
                        { label: 'All editions', value: 'all' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Button
                      type="primary"
                      icon={<VideoCameraOutlined />}
                      loading={isGenerating}
                      disabled={
                        uiMode === 'simple'
                          ? isLoadingSimpleConfig || !selectedSimpleLengthOption || Boolean(coverReferenceRequirementMessage)
                          : isLoadingModels || !selectedModelConfig || Boolean(coverReferenceRequirementMessage)
                      }
                      onClick={() => void handleGenerate()}
                      block
                    >
                      Generate Video
                    </Button>
                  </Col>
                  <Col xs={24} md={12} lg={8}>
                    <Button onClick={() => handleResetPrompts()} block>
                      Reset Recommended Prompts
                    </Button>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Button onClick={() => void copyText(prompt, 'Prompt copied to clipboard.')} block>
                      Copy Prompt
                    </Button>
                  </Col>
                </Row>
              </Space>
            </div>
          </Card>
        ) : (
          <Card
            size="small"
            title={libraryTitle}
            extra={
              <Space>
                <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
                  All versions
                </Button>
                <Tag>{videos.length} total</Tag>
                <Button icon={<ReloadOutlined />} loading={isLoadingVideos} onClick={() => void refreshVideos()}>
                  Refresh
                </Button>
              </Space>
            }
          >
            {videos.length === 0 ? (
              <Empty description="No marketing videos yet." />
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={10} lg={9}>
                    <Input
                      value={videoLibrarySearch}
                      onChange={event => setVideoLibrarySearch(event.target.value)}
                      placeholder="Search prompt, model, mode, or status"
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={12} md={7} lg={6}>
                    <Select
                      value={videoLibraryModeFilter}
                      options={videoModeFilterOptions}
                      onChange={value => setVideoLibraryModeFilter(value)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={7} lg={6}>
                    <Select
                      value={videoLibraryStatusFilter}
                      options={videoStatusFilterOptions}
                      onChange={value => setVideoLibraryStatusFilter(value)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} lg={3}>
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Tag>{filteredVideos.length} shown</Tag>
                    </div>
                  </Col>
                </Row>

                {filteredVideos.length ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {filteredVideos.map(video => {
                      const playbackUrl = resolveVideoPlaybackUrl({ apiBase, bookId, userId, video });
                      const latestVariantLabel = renderVariantLabel(video.latestVariant);
                      const videoTimestamp = formatVideoCardTimestamp(video);
                      return (
                        <button
                          key={video.id}
                          type="button"
                          onClick={() => {
                            setSelectedLibraryVideoId(video.id);
                            setVideoPlayerOpen(true);
                          }}
                          style={{
                            width: '100%',
                            padding: 8,
                            borderRadius: 10,
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: '#111827',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              aspectRatio: '16 / 9',
                              borderRadius: 8,
                              overflow: 'hidden',
                              background: '#000',
                              flexShrink: 0,
                            }}
                          >
                            {playbackUrl ? (
                              <video
                                key={playbackUrl}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'block',
                                  objectFit: 'cover',
                                  pointerEvents: 'none',
                                }}
                                src={playbackUrl}
                                muted
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <Text type="secondary">No preview</Text>
                            )}
                          </div>
                          <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                            <Tag color={statusColor(video.status)}>{(video.status || 'unknown').toUpperCase()}</Tag>
                            <Tag>{formatVideoModeLabel(video.mode)}</Tag>
                            {videoLibraryEditionScope === 'all' && video.language ? <Tag>{video.language}</Tag> : null}
                            {latestVariantLabel ? <Tag>{latestVariantLabel}</Tag> : null}
                            {video.inspirationSources?.length ? (
                              <Tag>{video.inspirationSources.length} source{video.inspirationSources.length === 1 ? '' : 's'}</Tag>
                            ) : null}
                          </Space>
                          {videoTimestamp ? (
                            <Text
                              type="secondary"
                              style={{
                                display: 'block',
                                marginTop: 8,
                                fontSize: 12,
                                lineHeight: 1.4,
                              }}
                            >
                              {videoTimestamp}
                            </Text>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Empty description="No videos match these filters." />
                )}
              </Space>
            )}
          </Card>
        )}
      </Space>

      <Modal
        title="Video Cost Details"
        open={videoCostBreakdownOpen}
        onCancel={() => setVideoCostBreakdownOpen(false)}
        footer={<Button onClick={() => setVideoCostBreakdownOpen(false)}>Close</Button>}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            Total: <strong>{activePricingEstimate.estimatedCredits.toLocaleString()} credits</strong>
          </Text>
          <Card size="small" styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text>Model: {(uiMode === 'simple' ? selectedSimpleLengthOption?.resolvedModelDisplayName : selectedModelConfig?.displayName) || 'Unknown'}</Text>
              <Text>Style: {getVideoStyleLabel(videoStyle, customVideoStyle)}</Text>
              <Text>{outputLabel}: {resolution || 'Unknown'}</Text>
              <Text>Aspect ratio: {effectiveAspectRatio}</Text>
              <Text>Final duration: {activePricingEstimate.finalDurationSeconds}s</Text>
              <Text>Billed duration: {activePricingEstimate.billedDurationSeconds}s</Text>
              <Text>Samples: {uiMode === 'simple' ? 1 : sampleCount}</Text>
              <Text>Audio: {(uiMode === 'simple' ? effectiveSimpleGenerateAudio : effectiveGenerateAudio) ? 'On' : 'Off'}</Text>
              {activePricingEstimate.generationPasses > 1 ? (
                <Text type="secondary">Includes a {COVER_ANIMATE_PRIMER_SECONDS}s primer pass before the main clip.</Text>
              ) : null}
              {activePricingEstimate.pricingKind === 'token_formula' ? (
                <>
                  <Text>
                    Estimated tokens: {activePricingEstimate.estimatedTokens ? Math.round(activePricingEstimate.estimatedTokens).toLocaleString() : 'Unknown'}
                  </Text>
                  <Text>
                    Pricing model: token-based formula converted to ${activePricingEstimate.estimatedUsd.toFixed(2)} and then rounded up to credits.
                  </Text>
                </>
              ) : (
                <>
                  <Text>
                    Base rate: ${activePricingEstimate.usdPerSecond.toFixed(4)} per billed second
                  </Text>
                  <Text>
                    Formula: ${activePricingEstimate.usdPerSecond.toFixed(4)} x {activePricingEstimate.billedDurationSeconds}s x {uiMode === 'simple' ? 1 : sampleCount} sample(s) = ${activePricingEstimate.estimatedUsd.toFixed(2)}
                  </Text>
                </>
              )}
              <Text>Converted credits: {activePricingEstimate.estimatedCredits.toLocaleString()}</Text>
            </Space>
          </Card>
          <Text type="secondary">
            Current credits: {creditBalance ? availableCredits.toLocaleString() : 'Loading...'}
          </Text>
        </Space>
      </Modal>

      <Modal
        title="Video Player"
        open={videoPlayerOpen}
        onCancel={() => setVideoPlayerOpen(false)}
        footer={selectedLibraryVideo ? [
          <Button
            key="download"
            onClick={() => void handleDownload(selectedLibraryVideo)}
            icon={<DownloadOutlined />}
            disabled={!selectedLibraryVideo.signedUrl && !selectedLibraryVideo.latestHistoryId}
          >
            Download
          </Button>,
          <Popconfirm
            key="delete"
            title="Delete this video and all versions?"
            description="This removes the generation record and every saved version. To remove only one version, use All versions."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => void handleDeleteVideo(selectedLibraryVideo)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>,
          <Button key="close" onClick={() => setVideoPlayerOpen(false)}>
            Close
          </Button>,
        ] : null}
        width={980}
      >
        {selectedLibraryVideo ? (
          (() => {
            const playbackUrl = resolveVideoPlaybackUrl({
              apiBase,
              bookId,
              userId,
              video: selectedLibraryVideo,
            });
            const resolvedGenerationPasses = resolveGenerationPasses(
              selectedLibraryVideo.mode,
              selectedLibraryVideo.generationPasses
            );
            const resolvedFinalDurationSeconds = resolveFinalDurationSeconds(
              selectedLibraryVideo.durationSeconds,
              selectedLibraryVideo.mode,
              selectedLibraryVideo.generationPasses,
              selectedLibraryVideo.finalDurationSeconds
            );
            const resolvedBilledDurationSeconds = resolveBilledDurationSeconds(
              selectedLibraryVideo.finalDurationSeconds ?? selectedLibraryVideo.durationSeconds,
              selectedLibraryVideo.mode,
              selectedLibraryVideo.billedDurationSeconds
            );
            const latestVariantLabel = renderVariantLabel(selectedLibraryVideo.latestVariant);

            return (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {selectedLibraryVideo.error ? (
                  <Alert type="error" showIcon message={selectedLibraryVideo.error} />
                ) : null}

                {playbackUrl ? (
                  <div
                    style={{
                      background: '#000',
                      borderRadius: 8,
                      padding: 12,
                      position: 'relative',
                    }}
                  >
                    {videoPlayerLoading ? (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--overlay-backdrop)',
                          borderRadius: 6,
                          zIndex: 1,
                          padding: 24,
                        }}
                      >
                        <LoadingCard label="Loading video preview..." style={overlayCardStyle} />
                      </div>
                    ) : null}
                    <video
                      style={{ width: '100%', maxHeight: '70vh', borderRadius: 6, background: '#000' }}
                      src={playbackUrl}
                      controls
                      preload="metadata"
                      onLoadedData={() => setVideoPlayerLoading(false)}
                      onCanPlay={() => setVideoPlayerLoading(false)}
                      onError={() => setVideoPlayerLoading(false)}
                    />
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No playable video preview is available." />
                )}

                <Space wrap>
                  <Tag color={statusColor(selectedLibraryVideo.status)}>
                    {(selectedLibraryVideo.status || 'unknown').toUpperCase()}
                  </Tag>
                  <Tag>{formatVideoModeLabel(selectedLibraryVideo.mode)}</Tag>
                  {selectedLibraryVideo.uiMode ? (
                    <Tag>{selectedLibraryVideo.uiMode === 'simple' ? 'Simple' : 'Advanced'}</Tag>
                  ) : null}
                  {latestVariantLabel ? <Tag>{latestVariantLabel}</Tag> : null}
                  <Tag>{selectedLibraryVideo.model || 'model'}</Tag>
                  <Tag>{resolvedFinalDurationSeconds ? `${resolvedFinalDurationSeconds}s final` : '?s final'}</Tag>
                  {resolvedGenerationPasses > 1 ? <Tag>{COVER_ANIMATE_PRIMER_SECONDS}s primer</Tag> : null}
                  {resolvedBilledDurationSeconds ? <Tag>{resolvedBilledDurationSeconds}s billed</Tag> : null}
                  <Tag>{selectedLibraryVideo.aspectRatio || '?'}</Tag>
                  <Tag>{selectedLibraryVideo.resolution || '?'}</Tag>
                  <Tag>{selectedLibraryVideo.generateAudio ? 'Audio On' : 'Audio Off'}</Tag>
                </Space>

                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    Created: {selectedLibraryVideo.createdAt ? new Date(selectedLibraryVideo.createdAt).toLocaleString() : 'Unknown time'}
                  </Text>
                  {selectedLibraryVideo.historyCount ? (
                    <Text type="secondary">History items: {selectedLibraryVideo.historyCount}</Text>
                  ) : null}
                </Space>

                <Card size="small" styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Text strong>Prompt</Text>
                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedLibraryVideo.prompt || 'No prompt saved'}
                    </Text>
                  </Space>
                </Card>
              </Space>
            );
          })()
        ) : (
          <Empty description="Select a video from the gallery." />
        )}
      </Modal>

      <Modal
        title="Choose Character Portrait References"
        open={characterReferenceModalOpen}
        onCancel={() => setCharacterReferenceModalOpen(false)}
        footer={<Button onClick={() => setCharacterReferenceModalOpen(false)}>Done</Button>}
        width={1100}
      >
        <Row gutter={16} align="top">
          <Col xs={24} lg={15}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>Available Portraits</Text>
                <Text type="secondary">
                  Filter saved Character Studio images, then select up to {MAX_CHARACTER_REFERENCE_ASSETS} portraits.
                </Text>
              </Space>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Input
                    value={characterReferenceSearch}
                    onChange={event => setCharacterReferenceSearch(event.target.value)}
                    placeholder="Search character, style, or prompt"
                    allowClear
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Select
                    value={characterReferenceCharacterFilter}
                    onChange={value => setCharacterReferenceCharacterFilter(value)}
                    options={characterReferenceCharacterOptions}
                  />
                </Col>
              </Row>
              <div
                style={{
                  maxHeight: 520,
                  overflowY: 'auto',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 10,
                  padding: 12,
                  background: 'var(--surface-muted)',
                }}
              >
                {isLoadingCharacterAssets ? (
                  <Text type="secondary">Loading saved portrait assets…</Text>
                ) : filteredCharacterAssetGroups.length ? (
                  <Space direction="vertical" size={14} style={{ width: '100%' }}>
                    {filteredCharacterAssetGroups.map(group => (
                      <div key={`portrait-group-${group.characterId}`}>
                        <Text strong>{group.characterName}</Text>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 10,
                            marginTop: 8,
                          }}
                        >
                          {group.items.map(asset => {
                            const selected = selectedCharacterReferenceIds.includes(asset.id);
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => toggleCharacterReferenceAsset(asset)}
                                style={{
                                  width: 104,
                                  borderRadius: 10,
                                  border: selected
                                    ? '1px solid var(--accent-primary, #1677ff)'
                                    : '1px solid var(--border-subtle)',
                                  padding: 6,
                                  background: selected ? 'rgba(22, 119, 255, 0.12)' : 'var(--surface-raised)',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                              >
                                <img
                                  src={resolveCharacterAssetPreviewSrc(asset)}
                                  alt={asset.characterName || 'Character reference'}
                                  style={{
                                    width: '100%',
                                    aspectRatio: asset.aspectRatio === '1:1' ? '1 / 1' : '2 / 3',
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    background: 'rgba(255,255,255,0.04)',
                                  }}
                                />
                                <Text
                                  style={{
                                    display: 'block',
                                    fontSize: 12,
                                    marginTop: 6,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {asset.stylePreset || 'Portrait'}
                                </Text>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No portrait assets match the current filters."
                  />
                )}
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={9}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>Selected References</Text>
                <Text type="secondary">
                  {selectedCharacterReferenceAssets.length} of {MAX_CHARACTER_REFERENCE_ASSETS} selected
                </Text>
              </Space>
              <div
                style={{
                  maxHeight: 520,
                  overflowY: 'auto',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 10,
                  padding: 12,
                  background: 'var(--surface-muted)',
                }}
              >
                {selectedCharacterReferenceAssets.length ? (
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    {selectedCharacterReferenceAssets.map(asset => (
                      <div
                        key={`selected-modal-reference-${asset.id}`}
                        style={{
                          display: 'flex',
                          gap: 10,
                          border: '1px solid var(--border-subtle)',
                          background: 'var(--surface-raised)',
                          borderRadius: 10,
                          padding: 8,
                        }}
                      >
                        <img
                          src={resolveCharacterAssetImageSrc(asset)}
                          alt={asset.characterName || 'Character reference'}
                          style={{
                            width: 68,
                            aspectRatio: asset.aspectRatio === '1:1' ? '1 / 1' : '2 / 3',
                            objectFit: 'cover',
                            borderRadius: 8,
                            background: 'rgba(255,255,255,0.04)',
                            flexShrink: 0,
                          }}
                        />
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text strong>{asset.characterName || 'Character'}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {asset.stylePreset || 'Portrait'} {asset.aspectRatio ? `| ${asset.aspectRatio}` : ''}
                          </Text>
                          {asset.prompt ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {asset.prompt.slice(0, 120)}
                              {asset.prompt.length > 120 ? '…' : ''}
                            </Text>
                          ) : null}
                          <Button size="small" onClick={() => toggleCharacterReferenceAsset(asset)}>
                            Remove
                          </Button>
                        </Space>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Select portraits on the left to add them as reference images."
                  />
                )}
              </div>
            </Space>
          </Col>
        </Row>
      </Modal>

      <MarketingVideoHistoryScreen
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        apiBase={apiBase}
        bookId={bookId}
        userId={userId}
        language={resolvedLanguage}
      />
    </>
  );
};

export default MarketingVideoModule;
